import TroisBarres from "../components/charte/TroisBarres";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { noterActivite, EXPORTS } from "../utilities/journalActivite";
import internalApi from "../utilities/internalApi";
import locationApi from '../utilities/locationApi';
import { getMotifsRejet, publierQuete, cloturerQuete, supprimerQuete, designerGagnant, exporterSoumissionsCsv } from '../utilities/locationService';
import { TonStatut, statutPaiement, statutQuete, statutSoumission } from "../utilities/statutsQuete";
/* Leaflet du paquet npm, comme les autres cartes : plus d'injection de
   <script> depuis unpkg sur cette page. */
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { messageServeur } from '../utilities/userService';
import { useValeurTip, enCfa } from '../utilities/valeurTip';
import { estArchivee, texteConfirmationPublication } from '../utilities/publicationQuete';

// Vague 1 : motif de rejet obligatoire (liste contrôlée du serveur). Renvoie
// { motif, commentaire } ou null si annulé/invalide.
async function demanderMotifRejet(): Promise<{ motif: string; commentaire: string } | null> {
  // Le wrapper lève en cas d'échec : un échec retombe sur l'alerte ci-dessous.
  const motifs: any[] = (await getMotifsRejet().catch(() => [])) || [];
  if (!motifs.length) { window.alert("Référentiel des motifs indisponible."); return null; }
  const liste = motifs.map((m, i) => `${i + 1}. ${m.libelle} (${m.code})`).join("\n");
  const choix = window.prompt(`Motif de rejet — entrez le numéro ou le code :\n${liste}`);
  if (!choix) return null;
  const saisi = choix.trim();
  const m = motifs.find(x => x.code === saisi) || motifs[Number(saisi) - 1];
  if (!m) { window.alert("Motif inconnu."); return null; }
  let commentaire = "";
  if (m.commentaireObligatoire) {
    commentaire = window.prompt("Commentaire (obligatoire) :") || "";
    if (!commentaire.trim()) { window.alert("Commentaire obligatoire."); return null; }
  }
  return { motif: m.code, commentaire };
}
import { modeleTuilesLeaflet, sousDomainesLeaflet, attributionFond } from '../utilities/basemap';
import { libelleRegleVictoire } from '../utilities/regleVictoire';
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import {
  Box, Button, CircularProgress, Chip, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, FormControl,
  LinearProgress, MenuItem, Select, Snackbar, Tooltip, Typography,
} from "@mui/material";
import ArrowBackRoundedIcon       from "@mui/icons-material/ArrowBackRounded";
import EditRoundedIcon             from "@mui/icons-material/EditRounded";
import PublishRoundedIcon          from "@mui/icons-material/PublishRounded";
import UnpublishedRoundedIcon      from "@mui/icons-material/UnpublishedRounded";
import DeleteOutlineRoundedIcon    from "@mui/icons-material/DeleteOutlineRounded";
import CheckRoundedIcon            from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon            from "@mui/icons-material/CloseRounded";
import OpenInNewRoundedIcon        from "@mui/icons-material/OpenInNewRounded";
import BrokenImageOutlinedIcon     from "@mui/icons-material/BrokenImageOutlined";
import LockOutlinedIcon            from "@mui/icons-material/LockOutlined";
import ArchiveOutlinedIcon         from "@mui/icons-material/ArchiveOutlined";
import { useNavigate, useParams }  from "react-router-dom";
import { useDispatch }             from "react-redux";
import { loaderOff, loaderOn, openSnackbar, setPageTitle } from "../redux/actions";
import IntegriteBadges from "../components/IntegriteBadges";
import { buildPopupHtml } from "../utilities/popupSoumission";
import RelanceCredit, { CreditEchecBadge } from "../components/CreditEchec";
import { STYLE_TON } from '../theme/tribeTheme';
import BoutonAide from '../components/charte/BoutonAide';
import BlocFacturation from './facturation/BlocFacturation';
import { AIDE_FICHE_QUETE } from '../aide/textesAide';

/* ═══════════════════════════════════════════════
   DESIGN TOKENS
═══════════════════════════════════════════════ */
/* Couleurs de la charte (src/theme/tribeTheme.ts), mêmes clés qu'avant. */
const C = {
  bg:           "var(--c-bg)",
  card:         "var(--c-surface)",
  cardInner:    "var(--c-surface-2)",
  border:       "var(--c-border)",
  borderInput:  "var(--c-border)",
  accent:       "#1F1E1B",
  accentSoft:   "#57534A",
  accentBg:     "rgba(31,30,27,0.07)",
  accentBorder: "rgba(31,30,27,0.45)",
  text:         "#1F1E1B",
  sub:          "#57534A",
  label:        "#57534A",
  muted:        "#57534A",
  success:      "#356A0F",
  successBg:    "rgba(53,106,15,0.10)",
  successBdr:   "rgba(53,106,15,0.45)",
  gold:         "#7C5600",
  goldBg:       "rgba(124,86,0,0.10)",
  goldBdr:      "rgba(124,86,0,0.45)",
  error:        "#A23A0D",
  errorBg:      "rgba(162,58,13,0.08)",
  errorBdr:     "rgba(162,58,13,0.45)",
  row:          "rgba(31,30,27,0.03)",
  rowHover:     "rgba(31,30,27,0.06)",
  blue:         "#1D5FA4",
  blueBg:       "rgba(29,95,164,0.10)",
  blueBdr:      "rgba(29,95,164,0.45)",
  teal:         "#3D7F78",
  tealBg:       "rgba(61,127,120,0.10)",
  tealBdr:      "rgba(61,127,120,0.45)",
};

/* ═══════════════════════════════════════════════
   TYPES
═══════════════════════════════════════════════ */
interface QuestChamp {
  label?: string;
  type?: string;
  obligatoire?: boolean | string;
  optionsJson?: string;
}

interface Quest {
  id?: string;
  titre: string;
  description?: string;
  logoUrl?: string;
  types: string[];
  champs?: QuestChamp[];
  statut: string;
  frequence?: string;
  dateDebut?: string;
  dateFin?: string;
  latMin?: number;
  latMax?: number;
  lngMin?: number;
  lngMax?: number;
  /* Polygone [lat, lng] : une quête peut n'avoir que lui comme zone. */
  zonePolygon?: number[][] | null;
  /* Nom de zone demandé par le client, conservé quand la résolution
     automatique échoue. Seules les quêtes issues du portail IA en ont un. */
  zoneDemandee?: string;
  tokensBase?: number;
  bonusN1Collecteurs?: number;
  bonusN1Multiplicateur?: number;
  bonusN2Collecteurs?: number;
  bonusN2Multiplicateur?: number;
  collectesAttendues?: number;
  collectesRealisees?: number;
  recompense?: number;   // alias éventuel
  challengeMode?: boolean;
  /* Challenge interne (D12) : récompense en Tips, gagnant désigné une fois. */
  challengeRecompenseTips?: number | null;
  challengeRegleVictoire?: string;
  challengeGagnantSoumissionId?: string | null;
  challengeGagnantEmail?: string | null;
  challengeGagnantPar?: string | null;
  challengeGagnantLe?: string | null;
  /* Récompense du gagnant non confirmée par user-service (relançable). */
  challengeRecompenseCreditee?: boolean | null;
  challengeCreditEchec?: boolean | null;
  challengeCreditEchecMessage?: string | null;
  challengeCreditEchecLe?: string | null;
  source?: string;
  budgetClient?: number;
  statutPaiement?: string;
  createdBy?: string;
  validationAuto?: boolean;
  cycleActif?: boolean;
  parentQuestId?: string | null;
  cycleNumero?: number;
  frequenceCycle?: string;
}

interface QuestStats {
  total?: number;
  collecteursActifs?: number;
  tokensDistribues?: number;
  valides?: number;
  rejetees?: number;
  enAttente?: number;
  expirees?: number;
}

interface Submission {
  id?: string;
  collecteurEmail?: string;
  collecteurNom?: string;
  createdAt?: string;
  photoUrl?: string;
  statut: string;
  tokensAttribues?: number;
  latitude?: number;
  longitude?: number;
  reponsesJson?: string;
  verdictVision?: any;
  signauxExif?: any;
  signauxIntegrite?: any;
  estTest?: boolean;
  /* Validée, mais crédit des Tips non confirmé par user-service (relançable). */
  credite?: boolean | null;
  creditEchec?: boolean | null;
  creditEchecMessage?: string | null;
  creditEchecLe?: string | null;
}

/* ═══════════════════════════════════════════════
   HELPERS
═══════════════════════════════════════════════ */
const QUEST_TYPE_LABELS: Record<string, string> = {
  poi: "Collecte de lieux", prix: "Relevé de prix", photo: "Photo terrain",
  enquete: "Enquête", verification: "Vérification de lieux",
  infrastructure: "Infrastructure", signal: "Signal réseau",
  sante: "Données sanitaires", agricole: "Données agricoles",
  commerce: "Recensement commerce",
};
const QUEST_TYPE_EMOJIS: Record<string, string> = {
  poi: "📍", prix: "🏷", photo: "📸", enquete: "📋",
  verification: "✅", infrastructure: "🏗", signal: "📶",
  sante: "🏥", agricole: "🌾", commerce: "🏪",
};

interface ColorCfg { bg: string; border: string; color: string; }

function fieldTypeStyle(type?: string): ColorCfg {
  switch (type?.toLowerCase()) {
    case "photo":  return { bg: C.blueBg,    border: C.blueBdr,    color: C.blue };
    case "gps":    return { bg: C.successBg, border: C.successBdr, color: C.success };
    case "prix":   return { bg: C.goldBg,    border: C.goldBdr,    color: C.gold };
    case "choix":  return { bg: C.accentBg,  border: C.accentBorder, color: C.accentSoft };
    case "nombre": return { bg: C.tealBg,    border: C.tealBdr,    color: C.teal };
    default:       return { bg: "rgba(31,30,27,0.05)", border: C.borderInput, color: C.sub };
  }
}

function fmtDate(d?: string): string {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return d; }
}

function fmtDateTime(d?: string): string {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString("fr-FR", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return d; }
}

function getId(o: { id?: string }): string { return o.id ?? ""; }

/* buildPopupHtml : utilities/popupSoumission (contenu Spotter échappé). */

function initials(email?: string): string {
  if (!email) return "?";
  const parts = email.split("@")[0].replace(/[._-]+/g, " ").trim().split(" ");
  return parts.slice(0, 2).map(p => p[0]?.toUpperCase() ?? "").join("") || "?";
}

/* Même masquage que le rapport PDF : trois caractères puis le domaine. */
function masquerEmail(email?: string | null): string {
  if (!email) return "—";
  return email.length > 3 ? email.slice(0, 3) + "***@" + (email.split("@")[1] ?? "?") : "***";
}

function isPublishedStatus(s: string): boolean {
  return ["publiee", "published", "active"].includes(s?.toLowerCase());
}

interface StatusCfg { label: string; bg: string; border: string; color: string; }

/* Libellés communs à la liste et au détail (utilities/statutsQuete) ; seules
   les couleurs sont propres à la page. */
const COULEURS: Record<TonStatut, Omit<StatusCfg, "label">> = STYLE_TON;

function questStatusStyle(s: string): StatusCfg {
  const { libelle, ton } = statutQuete(s);
  return { label: libelle, ...COULEURS[ton] };
}

function submissionStatusStyle(s: string): StatusCfg {
  const { libelle, ton } = statutSoumission(s);
  return { label: libelle, ...COULEURS[ton] };
}

/* Polygone exploitable : au moins trois sommets [lat, lng]. */
function polygoneDe(q: Quest): [number, number][] {
  return (q.zonePolygon ?? [])
    .filter(p => Array.isArray(p) && p.length >= 2 && Number.isFinite(p[0]) && Number.isFinite(p[1]))
    .map(p => [p[0], p[1]] as [number, number]);
}

function aUnRectangle(q: Quest): boolean {
  return q.latMin != null && q.latMax != null && q.lngMin != null && q.lngMax != null;
}

/* Description courte de la zone, pour les documents. */
function texteZone(q: Quest): string | null {
  if (aUnRectangle(q)) return `${q.latMin}°N ${q.lngMin}°E → ${q.latMax}°N ${q.lngMax}°E`;
  const poly = polygoneDe(q);
  return poly.length >= 3 ? `Polygone de ${poly.length} sommets` : null;
}

/* ═══════════════════════════════════════════════
   SUB-COMPONENTS
═══════════════════════════════════════════════ */
function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <Typography component="h2" sx={{
      fontFamily: "var(--font-display)", fontSize: "14px", fontWeight: 700,
      color: C.text, mb: 1.5, display: "flex", alignItems: "center", gap: 1,
    }}>
      <TroisBarres taille="s" />
      {children}
    </Typography>
  );
}

function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box sx={{ display: "flex", gap: 1.5, mb: "10px", alignItems: "flex-start" }}>
      <Typography sx={{ fontSize: "11px", color: C.label, flexShrink: 0, width: 130, pt: "1px" }}>
        {label}
      </Typography>
      <Box sx={{ fontSize: "13px", color: C.text, flex: 1 }}>
        {children}
      </Box>
    </Box>
  );
}

function KpiCard({ label, value, sub, color }: {
  label: string; value: React.ReactNode; sub?: string; color?: string;
}) {
  return (
    <Box sx={{
      background: C.card, borderRadius: "8px",
      border: "1.5px solid #1F1E1B", p: 2, flex: 1, minWidth: 0,
    }}>
      <Typography sx={{ fontSize: "10px", fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: C.label, mb: "6px" }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "26px", fontWeight: 800, color: color ?? C.text, lineHeight: 1.1 }}>
        {value}
      </Typography>
      {sub && <Typography sx={{ fontSize: "11px", color: C.sub, mt: "4px" }}>{sub}</Typography>}
    </Box>
  );
}

function StatusBadge({ cfg }: { cfg: StatusCfg }) {
  return (
    <Box sx={{
      display: "inline-flex", alignItems: "center", gap: "5px",
      background: cfg.bg, border: `1px solid ${cfg.border}`,
      borderRadius: "5px", px: "8px", py: "3px",
    }}>
      <Box sx={{ width: 5, height: 5, borderRadius: "50%", background: cfg.color, flexShrink: 0 }} />
      <Typography sx={{ fontSize: "11px", fontWeight: 600, color: cfg.color, lineHeight: 1 }}>
        {cfg.label}
      </Typography>
    </Box>
  );
}

/* ═══════════════════════════════════════════════
   PAGE
═══════════════════════════════════════════════ */
export default function QuestDetail() {
  const { id }    = useParams<{ id: string }>();
  const navigate  = useNavigate();
  const dispatch  = useDispatch();

  const [quest,        setQuest]        = useState<Quest | null>(null);
  const [stats,        setStats]        = useState<QuestStats>({});
  const [submissions,  setSubmissions]  = useState<Submission[]>([]);
  const [loading,      setLoading]      = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [subLoading,   setSubLoading]   = useState(true);
  const [unpublishing, setUnpublishing] = useState(false);
  const [deleting,     setDeleting]     = useState(false);
  const [generating,   setGenerating]   = useState(false);
  const [exportant,    setExportant]    = useState(false);
  const [cloturant,         setCloturant]         = useState(false);
  const valeurTip = useValeurTip();
  const [actionId,          setActionId]          = useState<string | null>(null);
  const [updatingPaiement,   setUpdatingPaiement]   = useState(false);

  // "must unpublish to edit" dialog
  const [editDialog,   setEditDialog]   = useState(false);

  // generic confirm dialog (delete)
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean; title: string; body: string; onConfirm: () => void;
  }>({ open: false, title: "", body: "", onConfirm: () => {} });

  // Winner dialog (challenge mode)
  const [winnerDialogOpen,  setWinnerDialogOpen]  = useState(false);
  const [selectedWinner,    setSelectedWinner]    = useState("");
  const [designatingWinner, setDesignatingWinner] = useState(false);
  const [winnerSnackbar,    setWinnerSnackbar]    = useState(false);

  const [cycles,            setCycles]            = useState<any[]>([]);
  const [cyclesLoading,     setCyclesLoading]     = useState(false);

  // ── Live map ──
  const liveMapContainerRef = useRef<HTMLDivElement>(null);
  const liveMapRef          = useRef<any>(null);
  const liveMarkersRef      = useRef<Record<string, any>>({});
  const mapSubsRef          = useRef<Submission[]>([]);
  const [lastRefreshAt, setLastRefreshAt] = useState<Date | null>(null);
  const [secSince,      setSecSince]      = useState(0);
  const [mapLoading,    setMapLoading]    = useState(true);
  const [liveMapReady,  setLiveMapReady]  = useState(false);
  const mapSubs = submissions;

  // Leaflet zone map
  const zoneContainerRef = useRef<HTMLDivElement>(null);
  const zoneMapRef       = useRef<any>(null);

  /* ── Loaders ──
     Un seul client HTTP (locationApi) : les fetch() à URL en dur
     contournaient l'intercepteur 401/403. */
  const loadQuest = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await locationApi.get(`/quest/${id}`);
      setQuest(res.data);
    } catch (err: any) {
      dispatch(openSnackbar(messageServeur(err, "Impossible de charger la quête."), "red"));
    } finally { setLoading(false); }
  }, [id]);

  /* « silencieux » : rafraîchissement périodique, sans remplacer le tableau
     par un indicateur de chargement toutes les 30 s. */
  const loadStats = useCallback(async (silencieux = false) => {
    if (!id) return;
    if (!silencieux) setStatsLoading(true);
    try {
      const res = await locationApi.get(`/quest/${id}/submissions/stats`);
      setStats(res.data ?? {});
    } catch { /* silencieux */ }
    finally { setStatsLoading(false); }
  }, [id]);

  /* Seule lecture des soumissions de la page : le tableau, la carte et le
     rapport partagent cette liste (elle était chargée trois fois). */
  const loadSubmissions = useCallback(async (silencieux = false) => {
    if (!id) return;
    if (!silencieux) setSubLoading(true);
    try {
      const res = await locationApi.get(`/quest/${id}/submissions`);
      setSubmissions(Array.isArray(res.data) ? res.data : []);
      setLastRefreshAt(new Date());
      setSecSince(0);
    } catch { /* silencieux */ }
    finally { setSubLoading(false); }
  }, [id]);

  const refreshAll = useCallback(() => {
    loadQuest(); loadStats(); loadSubmissions();
  }, [loadQuest, loadStats, loadSubmissions]);

  useEffect(() => { refreshAll(); }, [refreshAll]);

  /* Rafraîchissement périodique : soumissions et compteurs, rien d'autre. */
  useEffect(() => {
    if (!id) return;
    const minuterie = setInterval(() => { loadSubmissions(true); loadStats(true); }, 30000);
    const secondes  = setInterval(() => setSecSince(s => s + 1), 1000);
    return () => { clearInterval(minuterie); clearInterval(secondes); };
  }, [id, loadSubmissions, loadStats]);

  useEffect(() => {
    if (quest?.titre) dispatch(setPageTitle(quest.titre));
    return () => { dispatch(setPageTitle("")); };
  }, [quest?.titre]);

  /* ── Cycles ── */
  useEffect(() => {
    if (!quest?.cycleActif || !id) return;
    setCyclesLoading(true);
    locationApi.get(`/quest/${id}/cycles`)
      .then(r => setCycles(Array.isArray(r.data) ? r.data : []))
      .catch(() => {})
      .finally(() => setCyclesLoading(false));
  }, [quest?.cycleActif, id]);

  /* ── Live map ──
     La carte ne charge plus rien elle-même : elle trace la liste des
     soumissions de la page (loadSubmissions). */
  useEffect(() => {
    if (!quest || !id) return;
    let mounted = true;

    // Popup CSS (once)
    const STYLE_ID = "tribe-popup-style";
    if (!document.getElementById(STYLE_ID)) {
      const s = document.createElement("style"); s.id = STYLE_ID;
      s.textContent = `.tribe-popup .leaflet-popup-content-wrapper{background:var(--c-surface);border:1px solid rgba(31,30,27,0.35);border-radius:10px;box-shadow:0 8px 32px rgba(0,0,0,0.6);padding:0}.tribe-popup .leaflet-popup-tip{background:var(--c-surface)}.tribe-popup .leaflet-popup-content{margin:12px 14px}`;
      document.head.appendChild(s);
    }

    // Register popup action globals
    (window as any).__tribeValidateSub = async (subId: string) => {
      try {
        // Décision D5 : confirmation du montant avant tout crédit.
        const cible = mapSubsRef.current.find(s => s.id === subId);
        const qui = cible?.collecteurNom || cible?.collecteurEmail || "ce Spotter";
        if (!window.confirm(`${cible?.tokensAttribues ?? 0} Tips seront crédités à ${qui}. Confirmer la validation ?`)) return;
        await locationApi.put(`/submission/${subId}/validate`);
        liveMarkersRef.current[subId]?.closePopup();
        loadSubmissions(true); loadStats(true);
      } catch (e) {
        // Échec visible : la popup laissait croire que l'action avait abouti.
        window.alert("Action refusée par le serveur : la soumission n'a pas été modifiée.");
      }
    };
    (window as any).__tribeRejectSub = async (subId: string) => {
      try {
        const motif = await demanderMotifRejet();
        if (!motif) return;
        await locationApi.put(`/submission/${subId}/reject`, motif);
        liveMarkersRef.current[subId]?.closePopup();
        loadSubmissions(true); loadStats(true);
      } catch (e) {
        // Échec visible : la popup laissait croire que l'action avait abouti.
        window.alert("Action refusée par le serveur : la soumission n'a pas été modifiée.");
      }
    };

    setMapLoading(true);
    if (liveMapContainerRef.current && !liveMapRef.current
        && !(liveMapContainerRef.current as any)._leaflet_id) {
      const map = L.map(liveMapContainerRef.current, {
        zoomControl: true, scrollWheelZoom: true, attributionControl: false,
      });
      L.tileLayer(modeleTuilesLeaflet(true), {
        maxZoom: 19,
        subdomains: sousDomainesLeaflet(),
        attribution: attributionFond,
      }).addTo(map);
      const forme = { color: "#1F1E1B", weight: 2, fillColor: "#1F1E1B", fillOpacity: 0.08 };
      const poly = polygoneDe(quest);
      if (aUnRectangle(quest)) {
        const bounds: [[number, number], [number, number]] =
          [[quest.latMin!, quest.lngMin!], [quest.latMax!, quest.lngMax!]];
        L.rectangle(bounds, forme).addTo(map);
        map.fitBounds(bounds, { padding: [30, 30] });
      } else if (poly.length >= 3) {
        const p = L.polygon(poly, forme).addTo(map);
        map.fitBounds(p.getBounds(), { padding: [30, 30] });
      } else {
        map.setView([14.7167, -17.4677], 12);
      }
      liveMapRef.current = map;
      if (mounted) { setMapLoading(false); setLiveMapReady(true); }
    }

    return () => {
      mounted = false;
      try { if (liveMapRef.current) { liveMapRef.current.remove(); liveMapRef.current = null; } } catch {}
      liveMarkersRef.current = {};
      setLiveMapReady(false);
      delete (window as any).__tribeValidateSub;
      delete (window as any).__tribeRejectSub;
    };
  }, [quest?.id, id]);

  /* Marqueurs : retracés à chaque nouvelle liste de soumissions. */
  useEffect(() => {
    mapSubsRef.current = submissions;
    const map = liveMapRef.current;
    if (!liveMapReady || !map) return;
    submissions.forEach(sub => {
      const lat  = sub.latitude  ?? (sub as any).lat;
      const lng  = sub.longitude ?? (sub as any).lng;
      if (lat == null || lng == null) return;
      const color = sub.statut === "valide" ? "#356A0F" : sub.statut === "rejete" ? "#A23A0D"
                  : sub.statut === "expire" ? "#57534A" : "#7C5600";
      const sid   = sub.id ?? "";
      if (liveMarkersRef.current[sid]) {
        liveMarkersRef.current[sid].setStyle({ fillColor: color });
        liveMarkersRef.current[sid].setPopupContent(buildPopupHtml(sub));
      } else {
        const marker = L.circleMarker([lat, lng], {
          radius: 8, fillColor: color, color: "#ffffff", weight: 1.5, fillOpacity: 0.9,
        });
        marker.bindPopup(buildPopupHtml(sub), { className: "tribe-popup", maxWidth: 270 });
        marker.addTo(map);
        if (sid) liveMarkersRef.current[sid] = marker;
      }
    });
  }, [submissions, liveMapReady]);

  /* ── Zone map ── */
  useEffect(() => {
    if (!quest) return;
    const poly = polygoneDe(quest);
    if (!aUnRectangle(quest) && poly.length < 3) return;
    if (!zoneContainerRef.current || zoneMapRef.current) return;
    if ((zoneContainerRef.current as any)._leaflet_id) return;

    const map = L.map(zoneContainerRef.current, {
      zoomControl:       false,
      dragging:          false,
      touchZoom:         false,
      scrollWheelZoom:   false,
      doubleClickZoom:   false,
      boxZoom:           false,
      keyboard:          false,
      attributionControl: false,
    });

    L.tileLayer(modeleTuilesLeaflet(true), { subdomains: sousDomainesLeaflet() }).addTo(map);

    const forme = { color: C.accent, weight: 2, fillColor: C.accent, fillOpacity: 0.10 };
    if (aUnRectangle(quest)) {
      const bounds: [[number, number], [number, number]] =
        [[quest.latMin!, quest.lngMin!], [quest.latMax!, quest.lngMax!]];
      L.rectangle(bounds, forme).addTo(map);
      map.fitBounds(bounds, { padding: [20, 20] });
    } else {
      const p = L.polygon(poly, forme).addTo(map);
      map.fitBounds(p.getBounds(), { padding: [20, 20] });
    }
    zoneMapRef.current = map;

    return () => {
      try {
        if (zoneMapRef.current) {
          zoneMapRef.current.remove();
          zoneMapRef.current = null;
        }
      } catch (e) {
        zoneMapRef.current = null;
      }
    };
  }, [quest]);

  /* ── Valider et publier ──
     Une seule action (Q8) : le serveur fait de /validate et /publish la même
     chose (contrôles, e-mail client, push Spotters, journal). La confirmation
     redit le prix engagé (D3) ; un refus du serveur est affiché tel quel. */
  const doPublish = async () => {
    if (!quest || !id) return;
    dispatch(loaderOn());
    try {
      await publierQuete(quest.id || id);
      dispatch(openSnackbar(`"${quest.titre}" validée et publiée.`, "green"));
      loadQuest();
    } catch (err: any) {
      dispatch(openSnackbar(messageServeur(err, "Publication impossible."), "red"));
    } finally { dispatch(loaderOff()); }
  };

  const handlePublish = () => {
    if (!quest || !id) return;
    setConfirmDialog({
      open: true,
      title: "Valider et publier la quête ?",
      body: texteConfirmationPublication(quest),
      onConfirm: () => { setConfirmDialog(d => ({ ...d, open: false })); doPublish(); },
    });
  };

  /* ── Clôturer (Q9) : archivée, soumissions et paiements conservés ── */
  const handleCloturer = () => {
    if (!quest || !id) return;
    setConfirmDialog({
      open: true,
      title: "Clôturer la quête ?",
      body: `"${quest.titre}" sera archivée : elle n'est plus proposée aux Spotters, ses soumissions et paiements sont conservés.`,
      onConfirm: async () => {
        setConfirmDialog(d => ({ ...d, open: false }));
        setCloturant(true);
        dispatch(loaderOn());
        try {
          await cloturerQuete(quest.id || id);
          dispatch(openSnackbar(`"${quest.titre}" clôturée.`, "green"));
          loadQuest();
        } catch (err: any) {
          dispatch(openSnackbar(messageServeur(err, "Clôture impossible."), "red"));
        } finally {
          setCloturant(false);
          dispatch(loaderOff());
        }
      },
    });
  };

  /* ── Unpublish ── */
  const doUnpublish = async (): Promise<boolean> => {
    if (!quest || !id) return false;
    setUnpublishing(true);
    dispatch(loaderOn());
    try {
      await locationApi.put(`/quest/${id}/unpublish`);
      dispatch(openSnackbar(`"${quest.titre}" dépubliée.`, "green"));
      loadQuest();
      return true;
    } catch (err: any) {
      dispatch(openSnackbar(messageServeur(err, "Dépublication impossible."), "red"));
      return false;
    } finally {
      setUnpublishing(false);
      dispatch(loaderOff());
    }
  };

  /* ── Edit ── */
  const handleEdit = () => {
    if (!quest || !id) return;
    if (isPublishedStatus(quest.statut)) {
      setEditDialog(true);
    } else {
      navigate(`/quetes/creer?id=${id}`);
    }
  };

  const handleUnpublishAndEdit = async () => {
    setEditDialog(false);
    const ok = await doUnpublish();
    if (ok) navigate(`/quetes/creer?id=${id}`);
  };

  /* ── Delete ──
     Sans dépublication préalable : le serveur refuse (400, texte affiché) une
     quête qui a des soumissions, et la dépublier d'abord l'aurait laissée en
     brouillon après le refus. */
  const doDelete = async () => {
    if (!quest || !id) return;
    setDeleting(true);
    dispatch(loaderOn());
    try {
      await supprimerQuete(quest.id || id);
      dispatch(openSnackbar(`"${quest.titre}" supprimée.`, "green"));
      dispatch(loaderOff());
      navigate("/quetes/liste");
    } catch (err: any) {
      dispatch(openSnackbar(messageServeur(err, "Suppression impossible."), "red"));
      setDeleting(false);
      dispatch(loaderOff());
    }
  };

  const handleDelete = () => {
    if (!quest) return;
    setConfirmDialog({
      open: true,
      title: "Supprimer la quête ?",
      body: `Êtes-vous sûr de vouloir supprimer "${quest.titre}" ? Cette action est irréversible. `
        + "Une quête qui a des soumissions ne se supprime pas : clôturez-la.",
      onConfirm: () => { setConfirmDialog(d => ({ ...d, open: false })); doDelete(); },
    });
  };

  /* ── Submission actions ── */
  const handleSubmissionAction = async (sub: Submission, action: "validate" | "reject", confirme = false) => {
    const sid = getId(sub);
    if (!sid) return;
    // Décision D5 : aucun crédit sans confirmation explicite du montant.
    if (action === "validate" && !confirme) {
      const qui = sub.collecteurNom || sub.collecteurEmail || "ce Spotter";
      setConfirmDialog({
        open: true,
        title: "Valider la soumission ?",
        body: `${sub.tokensAttribues ?? 0} Tips seront crédités à ${qui}. Le crédit est définitif.`,
        onConfirm: () => { setConfirmDialog(d => ({ ...d, open: false })); handleSubmissionAction(sub, "validate", true); },
      });
      return;
    }
    let body: { motif: string; commentaire: string } | undefined;
    if (action === "reject") {
      const motif = await demanderMotifRejet();
      if (!motif) return;
      body = motif;
    }
    setActionId(sid);
    try {
      await locationApi.put(`/submission/${sid}/${action}`, body);
      dispatch(openSnackbar(
        action === "validate" ? "Soumission validée." : "Soumission rejetée.",
        action === "validate" ? "green" : "red",
      ));
      loadSubmissions(true); loadStats(true);
    } catch (err: any) {
      dispatch(openSnackbar(messageServeur(err, "Action refusée par le serveur."), "red"));
    } finally { setActionId(null); }
  };

  /* ── Désigner le gagnant (challenge, D12) ──
     Parmi les soumissions validées seulement, une seule fois (409 ensuite).
     La récompense en Tips est créditée par le serveur : on la redit avant. */
  const candidatsGagnant = submissions.filter(s => s.statut?.toLowerCase() === "valide");

  const doDesignerGagnant = async (submissionId: string) => {
    if (!id) return;
    setDesignatingWinner(true);
    try {
      await designerGagnant(quest?.id || id, submissionId);
      setSelectedWinner("");
      setWinnerSnackbar(true);
      loadQuest();
    } catch (err: any) {
      dispatch(openSnackbar(messageServeur(err, "Désignation du gagnant impossible."), "red"));
    } finally {
      setDesignatingWinner(false);
    }
  };

  const handleDesignerGagnant = () => {
    if (!id || !selectedWinner) return;
    const sub = candidatsGagnant.find(s => getId(s) === selectedWinner);
    const qui = sub?.collecteurNom || sub?.collecteurEmail || "ce Spotter";
    const tips = Number(quest?.challengeRecompenseTips) || 0;
    const cfa = enCfa(tips, valeurTip);
    setWinnerDialogOpen(false);
    setConfirmDialog({
      open: true,
      title: "Confirmer le gagnant ?",
      body: tips > 0
        ? `${qui} sera désigné gagnant : ${tips.toLocaleString("fr-FR")} Tips${cfa != null ? ` (≈ ${cfa.toLocaleString("fr-FR")} F CFA)` : ""} seront crédités sur son solde. La désignation est définitive.`
        : `${qui} sera désigné gagnant. Aucune récompense en Tips n'est prévue pour ce Challenge. La désignation est définitive.`,
      onConfirm: () => { setConfirmDialog(d => ({ ...d, open: false })); doDesignerGagnant(selectedWinner); },
    });
  };

  /* ── KPI computed ── */
  const total      = stats.total           ?? submissions.length;
  const collecteurs = stats.collecteursActifs ?? 0;
  const tokens     = stats.tokensDistribues  ?? 0;
  const valides    = stats.valides ?? submissions.filter(s =>
    ["valide", "validee", "validated"].includes(s.statut?.toLowerCase())).length;
  const tauxValidation = total > 0 ? Math.round((valides / total) * 100) : 0;

  const realises   = quest?.collectesRealisees ?? 0;
  const attendues  = quest?.collectesAttendues ?? 0;
  const progressPct = attendues > 0 ? Math.min(100, Math.round((realises / attendues) * 100)) : 0;

  const qst        = quest ? questStatusStyle(quest.statut) : null;
  const isPublished = quest ? isPublishedStatus(quest.statut) : false;
  const archivee    = quest ? estArchivee(quest.statut) : false;

  /* Rectangle OU polygone : une quête à polygone seul est géorepérée par le
     serveur, elle ne doit pas s'afficher « sans zone ». */
  const zoneTexte = quest ? texteZone(quest) : null;
  const hasZone   = zoneTexte != null;

  /* ── Export CSV des soumissions (endpoint serveur, déjà réservé à
     l'administration et au client propriétaire) ── */
  const exporterCsv = async () => {
    if (!quest || !id) return;
    setExportant(true);
    try {
      const blob = await exporterSoumissionsCsv(quest.id || id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `export-${quest.id || id}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      noterActivite("export", EXPORTS.queteCsv);
    } catch (err: any) {
      dispatch(openSnackbar("Export CSV impossible.", "red"));
    } finally { setExportant(false); }
  };

  /* ── PDF Generation ── */
  const generatePDF = async () => {
    if (!quest) return;
    setGenerating(true);
    dispatch(loaderOn());
    try {
      /* ── Helpers ── */
      const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const W = 210; const H = 297;

      const fill = (r: number, g: number, b: number) => doc.setFillColor(r, g, b);
      const ink  = (r: number, g: number, b: number) => doc.setTextColor(r, g, b);
      const rect = (x: number, y: number, w: number, h: number) => doc.rect(x, y, w, h, "F");

      const fullBg = () => { fill(5, 5, 13); rect(0, 0, W, H); };   // var(--c-surface)
      const accentBar = (y: number, h = 0.8) => { fill(124, 58, 237); rect(0, y, W, h); };

      const fitText = (text: string, x: number, y: number, maxW: number, opts?: any) => {
        const lines = doc.splitTextToSize(text, maxW);
        doc.text(lines, x, y, opts);
        return lines.length;
      };

      /* ── Load logo as PNG data URL from SVG ── */
      let logoDataUrl: string | null = null;
      try {
        const svgText = await fetch("/assets/static/svg/applogo.svg").then(r => r.text());
        const parser  = new DOMParser();
        const svgDoc  = parser.parseFromString(svgText, "image/svg+xml");
        const svgEl   = svgDoc.documentElement;
        svgEl.querySelectorAll('path[fill="black"]:not([fill-rule]),path[fill="#000000"]:not([fill-rule]),text,tspan')
          .forEach(el => el.setAttribute("fill", "white"));
        svgEl.setAttribute("width", "120");
        svgEl.setAttribute("height", "120");
        const svgStr  = new XMLSerializer().serializeToString(svgEl);
        const svgBlob = new Blob([svgStr], { type: "image/svg+xml" });
        const blobUrl = URL.createObjectURL(svgBlob);
        logoDataUrl   = await new Promise<string | null>(res => {
          const img = new Image();
          img.onload = () => {
            const c = document.createElement("canvas"); c.width = 120; c.height = 120;
            const ctx = c.getContext("2d")!;
            ctx.fillStyle = "var(--c-surface)"; ctx.fillRect(0, 0, 120, 120);
            ctx.drawImage(img, 0, 0);
            URL.revokeObjectURL(blobUrl);
            res(c.toDataURL("image/png"));
          };
          img.onerror = () => { URL.revokeObjectURL(blobUrl); res(null); };
          img.src = blobUrl;
        });
      } catch { /* logo optional */ }

      /* ──────────────────────────────────────────
         PAGE 1 — Couverture
      ────────────────────────────────────────── */
      fullBg();

      // Logo
      if (logoDataUrl) { doc.addImage(logoDataUrl, "PNG", 15, 14, 22, 22); }
      else {
        doc.setFontSize(14); doc.setFont("helvetica", "bold");
        ink(124, 58, 237); doc.text("TRIBE", 15, 26);
      }

      // Barre horizontale violette sous le logo
      accentBar(40, 0.8);

      // Titre quête
      ink(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(28);
      fitText(quest.titre, W / 2, 120, 160, { align: "center" });

      // Commanditaire / sous-titre
      const commanditaire = (quest as any).commanditaire ?? "";
      if (commanditaire) {
        doc.setFontSize(14); doc.setFont("helvetica", "normal");
        ink(120, 120, 150);
        doc.text(commanditaire, W / 2, 137, { align: "center" });
      }

      // Types de collecte
      if (quest.types?.length) {
        doc.setFontSize(10); ink(100, 100, 130);
        doc.text(quest.types.map(t => QUEST_TYPE_LABELS[t] ?? t).join("  ·  "), W / 2, 148, { align: "center" });
      }

      // Date génération (bas de page)
      const today = new Date().toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
      ink(60, 60, 80);
      doc.setFontSize(9); doc.setFont("helvetica", "normal");
      doc.text(`Rapport généré le ${today} — Tribe Africa`, W / 2, H - 10, { align: "center" });

      // Barre accent bas de page
      fill(124, 58, 237); rect(0, H - 4, W, 4);

      /* ──────────────────────────────────────────
         PAGE 2 — Résumé exécutif
      ────────────────────────────────────────── */
      doc.addPage();
      fullBg();
      accentBar(0, 2);

      // Section header
      ink(167, 139, 250); doc.setFontSize(9); doc.setFont("helvetica", "bold");
      doc.text("RÉSUMÉ EXÉCUTIF", 15, 18);

      // 3 info cards (x, y, w, h, title, lines[])
      const drawCard = (x: number, y: number, w: number, h: number, title: string, lines: string[], color: [number,number,number]) => {
        fill(18, 18, 40); rect(x, y, w, h);
        doc.setDrawColor(color[0], color[1], color[2]);
        doc.roundedRect(x, y, w, h, 2, 2, "S");
        doc.setFontSize(8); doc.setFont("helvetica", "bold"); doc.setTextColor(color[0], color[1], color[2]);
        doc.text(title.toUpperCase(), x + 4, y + 7);
        doc.setFontSize(9); doc.setFont("helvetica", "normal"); ink(200, 200, 220);
        lines.forEach((l, i) => {
          const rows = doc.splitTextToSize(l, w - 8);
          doc.text(rows, x + 4, y + 14 + i * 7);
        });
      };

      // Rejetées comptées par le serveur : « total − validées » comptait les
      // soumissions en attente comme rejetées (Z6-10).
      const rejetes = stats.rejetees ?? submissions.filter(s => s.statut === "rejete").length;
      const pct = attendues > 0 ? Math.round((realises / attendues) * 100) : 0;

      // Stats enrichies — calculées une seule fois, disponibles pages 2 + 4
      const totalSoumissions = submissions.length;
      const validees     = submissions.filter(s => s.statut === 'valide').length;
      const rejetees     = submissions.filter(s => s.statut === 'rejete').length;
      const enAttente    = totalSoumissions - validees - rejetees;
      const tauxCompletion = (quest.collectesAttendues ?? 0) > 0
        ? Math.round(((quest.collectesRealisees ?? 0) / (quest.collectesAttendues ?? 1)) * 100) : 0;
      const dureeMission = (quest.dateDebut && quest.dateFin)
        ? Math.ceil((new Date(quest.dateFin).getTime() - new Date(quest.dateDebut).getTime()) / (1000 * 60 * 60 * 24))
        : 0;
      // D11 : le rapport est remis au client ; il ne porte aucun montant versé
      // aux Spotters (ni par Spotter, ni total). Les écrans admin les gardent.

      drawCard(14, 26, 56, 52, "Ce qu'on cherchait",
        [
          quest.types?.map(t => QUEST_TYPE_LABELS[t] ?? t).join(", ") ?? "—",
          `Zone : ${hasZone ? "Définie" : "Aucune zone"}`,
          `Cible : ${attendues} collectes`,
        ], [124, 58, 237]);

      drawCard(76, 26, 56, 52, "Ce qu'on a trouvé",
        [
          `${total} soumissions reçues`,
          `${stats.valides ?? valides} validées`,
          `${rejetes} rejetées`,
        ], [52, 211, 153]);

      drawCard(138, 26, 56, 52, "Performance",
        [
          `${pct}% complété`,
          `${tauxValidation}% de validation`,
          `${stats.collecteursActifs ?? 0} Spotters actifs`,
        ], [96, 165, 250]);

      // KPIs en grands chiffres
      const kpis = [
        { label: "Total soumissions",  value: String(total),                  color: [255, 255, 255] as [number,number,number] },
        { label: "Taux de validation", value: `${tauxValidation}%`,           color: [52, 211, 153] as [number,number,number] },
        { label: "Validées",           value: String(validees),                color: [167, 139, 250] as [number,number,number] },
        { label: "Spotters actifs", value: String(stats.collecteursActifs ?? 0), color: [96, 165, 250] as [number,number,number] },
      ];

      const kpiW = (W - 30) / 4;
      kpis.forEach((k, i) => {
        const kx = 15 + i * (kpiW + 2);
        const ky = 90;
        fill(18, 18, 40); rect(kx, ky, kpiW, 28);
        ink(k.color[0], k.color[1], k.color[2]);
        doc.setFontSize(20); doc.setFont("helvetica", "bold");
        doc.text(k.value, kx + kpiW / 2, ky + 16, { align: "center" });
        ink(100, 100, 130); doc.setFontSize(7); doc.setFont("helvetica", "normal");
        doc.text(k.label.toUpperCase(), kx + kpiW / 2, ky + 24, { align: "center" });
      });

      // Barre de complétion
      const barY = 128;
      ink(167, 139, 250); doc.setFontSize(9); doc.setFont("helvetica", "bold");
      doc.text(`Progression de la quête — ${pct}%`, 15, barY);
      fill(30, 30, 50); rect(15, barY + 3, W - 30, 5);
      fill(124, 58, 237); rect(15, barY + 3, Math.max(0, (W - 30) * pct / 100), 5);

      // Footer
      ink(60, 60, 80); doc.setFontSize(8);
      doc.text(`Tribe Africa · Rapport de quête · ${today}`, W / 2, H - 8, { align: "center" });
      fill(124, 58, 237); rect(0, H - 2, W, 2);

      /* ──────────────────────────────────────────
         PAGE 3 — Données terrain
      ────────────────────────────────────────── */
      doc.addPage();
      fullBg();
      accentBar(0, 2);

      ink(167, 139, 250); doc.setFontSize(9); doc.setFont("helvetica", "bold");
      doc.text("DONNÉES TERRAIN", 15, 18);

      // Tableau soumissions
      const tableHeaders = ["Spotter", "Date", "Statut"];
      const colW = [(W - 30) * 0.45, (W - 30) * 0.3, (W - 30) * 0.25];
      let ty = 28;

      // Header row
      fill(20, 20, 50); rect(15, ty - 5, W - 30, 8);
      ink(167, 139, 250); doc.setFontSize(8); doc.setFont("helvetica", "bold");
      let cx = 15;
      tableHeaders.forEach((h, i) => { doc.text(h, cx + 2, ty); cx += colW[i]; });
      ty += 6;

      // Rows
      const rowsToShow = submissions.slice(0, 30);
      rowsToShow.forEach((sub, ri) => {
        if (ty > H - 20) return;
        fill(ri % 2 === 0 ? 10 : 15, ri % 2 === 0 ? 10 : 15, ri % 2 === 0 ? 25 : 32);
        rect(15, ty - 4, W - 30, 7);

        const email = sub.collecteurEmail ?? "";
        const masked = email.length > 3 ? email.slice(0, 3) + "***@" + (email.split("@")[1] ?? "?") : "***";
        const status = statutSoumission(sub.statut).libelle;
        const statusColor: [number, number, number] =
          sub.statut?.toLowerCase() === "valide" ? [52, 211, 153] :
          sub.statut?.toLowerCase() === "rejete" ? [248, 113, 113] :
          sub.statut?.toLowerCase() === "expire" ? [156, 163, 175] : [245, 158, 11];

        ink(200, 200, 220); doc.setFontSize(7.5); doc.setFont("helvetica", "normal");
        cx = 15;
        doc.text(masked, cx + 2, ty); cx += colW[0];
        doc.text(fmtDate(sub.createdAt), cx + 2, ty); cx += colW[1];
        doc.setTextColor(statusColor[0], statusColor[1], statusColor[2]);
        doc.text(status, cx + 2, ty);
        ty += 7;
      });

      if (submissions.length > 30) {
        ink(100, 100, 130); doc.setFontSize(8);
        doc.text(`… et ${submissions.length - 30} autres soumissions`, 15, ty + 3);
        ty += 8;
      }

      // Photos grid (4 premières photos validées)
      const photosubs = submissions.filter(s => s.photoUrl && s.statut?.toLowerCase() === "valide").slice(0, 4);
      if (photosubs.length > 0 && ty < H - 70) {
        ty += 8;
        ink(167, 139, 250); doc.setFontSize(8); doc.setFont("helvetica", "bold");
        doc.text("PHOTOS VALIDÉES", 15, ty);
        ty += 5;
        // We'd need to load images; skip actual image rendering to avoid async complexity
        // Draw placeholder boxes instead
        photosubs.forEach((s, i) => {
          const px = 15 + (i % 2) * 95;
          const py = ty + Math.floor(i / 2) * 48;
          fill(20, 20, 45); rect(px, py, 88, 40);
          ink(60, 60, 90); doc.setFontSize(7);
          doc.text("Photo disponible dans l'application", px + 44, py + 20, { align: "center" });
          if (s.collecteurEmail) {
            ink(100, 100, 130); doc.setFontSize(6);
            doc.text(s.collecteurEmail.slice(0, 3) + "***", px + 2, py + 37);
          }
        });
      }

      ink(60, 60, 80); doc.setFontSize(8);
      doc.text(`Tribe Africa · Rapport de quête · ${today}`, W / 2, H - 8, { align: "center" });
      fill(124, 58, 237); rect(0, H - 2, W, 2);

      /* ──────────────────────────────────────────
         PAGE 4 — Analyse & Recommandations (IA)
      ────────────────────────────────────────── */
      doc.addPage();
      fullBg();
      accentBar(0, 2);

      ink(167, 139, 250); doc.setFontSize(9); doc.setFont("helvetica", "bold");
      doc.text("ANALYSE & RECOMMANDATIONS", 15, 18);

      // Tableau de synthèse. La colonne « Benchmark Tribe » (91 %, 78–82 %,
      // 8 jours) a été retirée : ces chiffres étaient écrits en dur, aucune
      // mesure ne les produisait (Z6-11).
      let sy = 26;
      const synthColX = [15, 100];
      fill(124, 58, 237); rect(15, sy, W - 30, 7);
      ink(255, 255, 255); doc.setFontSize(9); doc.setFont("helvetica", "bold");
      doc.text("Indicateur",       synthColX[0] + 2, sy + 5);
      doc.text("Valeur",           synthColX[1] + 2, sy + 5);
      sy += 7;
      const synthRows: [string, string][] = [
        ["Taux de complétion",  `${tauxCompletion}%`],
        ["Taux de validation",  `${tauxValidation}%`],
        ["Collectes validées",  `${validees} / ${quest.collectesAttendues ?? "?"}`],
        ["Collectes rejetées",  `${rejetees}`],
        ["Durée de la mission", dureeMission > 0 ? `${dureeMission} jours` : "—"],
      ];
      doc.setFontSize(9); doc.setFont("helvetica", "normal");
      synthRows.forEach((row, ri) => {
        fill(ri % 2 === 0 ? 12 : 18, ri % 2 === 0 ? 12 : 18, ri % 2 === 0 ? 30 : 40);
        rect(15, sy, W - 30, 7);
        doc.setDrawColor(50, 50, 80); doc.setLineWidth(0.2);
        doc.rect(15, sy, W - 30, 7, "S");
        ink(200, 200, 220);
        row.forEach((cell, ci) => doc.text(cell, synthColX[ci] + 2, sy + 5));
        sy += 7;
      });
      sy += 4;

      // Capture carte Leaflet
      try {
        if (liveMapContainerRef.current) {
          const canvas = await html2canvas(liveMapContainerRef.current, {
            useCORS: true, allowTaint: true, logging: false, scale: 1,
          });
          const mapImgData = canvas.toDataURL("image/png");
          const mapImgH = Math.min(45, Math.round((170 * canvas.height) / canvas.width));
          if (sy + mapImgH < H - 70) {
            doc.addImage(mapImgData, "PNG", 15, sy, 170, mapImgH);
            sy += mapImgH + 3;
            const lgX = W / 2 - 52;
            fill(52, 211, 153);  doc.rect(lgX,       sy - 2.5, 3, 3, "F");
            fill(245, 158, 11);  doc.rect(lgX + 38,  sy - 2.5, 3, 3, "F");
            fill(248, 113, 113); doc.rect(lgX + 82,  sy - 2.5, 3, 3, "F");
            ink(100, 100, 130); doc.setFontSize(7.5); doc.setFont("helvetica", "normal");
            doc.text("Validées",   lgX + 5,  sy);
            doc.text("En attente", lgX + 43, sy);
            doc.text("Rejetées",   lgX + 87, sy);
            sy += 6;
          }
        }
      } catch { /* TODO: capture carte */ }

      type RecItem = { titre: string; corps: string; impact?: string };
      let recItems: RecItem[] = [];
      let apiErrorMsg = "";
      // Q26 : recommandations demandées à admin-service, qui construit lui-même
      // la demande sans aucune donnée financière (ni budget, ni Tips) et
      // plafonne le coût. Aucune clé ni donnée ne part du navigateur.
      try {
        const r = await internalApi.post(`/rapport-ia/quete/${quest.id || id}`);
        const lignes = String(r.data?.texte ?? "")
          .split("\n")
          .map((l: string) => l.replace(/^\s*(?:[-*•]|\d+[.)])\s*/, "").replace(/\*\*/g, "").trim())
          .filter((l: string) => l.length > 15);
        recItems = lignes.slice(0, 3).map((l: string, i: number) => ({ titre: `Recommandation ${i + 1}`, corps: l }));
        if (recItems.length === 0) apiErrorMsg = "réponse vide";
      } catch (e: any) {
        apiErrorMsg = typeof e?.response?.data === "string" && e.response.data ? e.response.data : "service indisponible";
      }

      if (recItems.length === 0) {
        if (apiErrorMsg) {
          recItems = [{
            titre: "Recommandations non disponibles",
            corps: `Recommandations IA indisponibles : ${apiErrorMsg.slice(0, 120)}`,
            impact: "Faible",
          }];
        } else {
          recItems = [
            { titre: "Données insuffisantes", corps: "Vérifiez la configuration de l'API Intelligence.", impact: "Faible" },
            { titre: "Analyse des soumissions", corps: "Consultez les données dans le tableau de bord pour identifier les tendances.", impact: "Moyen" },
            { titre: "Engagement des Spotters", corps: "Augmentez le taux de complétion en communiquant directement avec les Spotters actifs.", impact: "Élevé" },
          ];
        }
      }

      const icons = ["01", "02", "03"];
      const recColors: [number,number,number][] = [[124,58,237],[52,211,153],[96,165,250]];
      let ry = sy;
      recItems.forEach((item, i) => {
        if (ry > H - 60) return;
        // Numéro
        fill(recColors[i][0], recColors[i][1], recColors[i][2]);
        rect(15, ry, 8, 8);
        ink(255, 255, 255); doc.setFontSize(8); doc.setFont("helvetica", "bold");
        doc.text(icons[i], 19, ry + 5.5, { align: "center" });

        // Box
        fill(15, 15, 35); rect(26, ry, W - 41, 42);
        // Titre
        doc.setTextColor(recColors[i][0], recColors[i][1], recColors[i][2]);
        doc.setFontSize(8); doc.setFont("helvetica", "bold");
        doc.text(item.titre || `Recommandation ${i + 1}`, 29, ry + 6);
        // Impact badge
        if (item.impact) {
          const ic: [number,number,number] = item.impact === "Élevé" ? [248,113,113] : item.impact === "Moyen" ? [245,158,11] : [52,211,153];
          ink(ic[0], ic[1], ic[2]); doc.setFontSize(7);
          doc.text(`Impact : ${item.impact}`, W - 44, ry + 6);
        }
        // Corps
        ink(200, 200, 220); doc.setFontSize(8); doc.setFont("helvetica", "normal");
        const recLines = doc.splitTextToSize(item.corps, W - 50);
        doc.text(recLines.slice(0, 4), 29, ry + 13);
        ry += 50;
      });

      // Footer IA
      ink(60, 60, 80); doc.setFontSize(7);
      doc.text("Recommandations générées automatiquement par Tribe", W / 2, H - 12, { align: "center" });
      doc.text(`Tribe Africa · Rapport de quête · ${today}`, W / 2, H - 8, { align: "center" });
      fill(124, 58, 237); rect(0, H - 2, W, 2);

      /* ──────────────────────────────────────────
         PAGE 5 — Méthodologie
      ────────────────────────────────────────── */
      doc.addPage();
      fullBg();
      accentBar(0, 2);

      ink(167, 139, 250); doc.setFontSize(9); doc.setFont("helvetica", "bold");
      doc.text("MÉTHODOLOGIE", 15, 18);

      let my = 28;
      const metaLine = (label: string, value: string, color: [number,number,number] = [200,200,220]) => {
        if (my > H - 20) return;
        ink(120, 120, 160); doc.setFontSize(8); doc.setFont("helvetica", "bold");
        doc.text(label + " :", 15, my);
        doc.setTextColor(color[0], color[1], color[2]);
        doc.setFont("helvetica", "normal");
        const lines = doc.splitTextToSize(value, W - 65);
        doc.text(lines, 65, my);
        my += Math.max(7, lines.length * 5);
      };

      if (quest.description) metaLine("Description", quest.description);
      metaLine("Types de collecte", (quest.types ?? []).map(t => QUEST_TYPE_LABELS[t] ?? t).join(", "));
      metaLine("Fréquence", quest.frequence ?? "—");
      metaLine("Date début", fmtDate(quest.dateDebut));
      metaLine("Date fin", fmtDate(quest.dateFin));
      if (quest.champs?.length)
        metaLine("Champs collectés", quest.champs.map(f => `${f.label ?? "?"} (${f.type ?? "texte"})`).join(", "));
      if ((quest as any).tokensBase)
        metaLine("Tips de base", String((quest as any).tokensBase));
      if (quest.bonusN1Collecteurs != null)
        metaLine("Bonus N1", `Top ${quest.bonusN1Collecteurs} × ${quest.bonusN1Multiplicateur}`);
      if (quest.bonusN2Collecteurs != null)
        metaLine("Bonus N2", `Rang ${(quest.bonusN1Collecteurs ?? 0) + 1}–${quest.bonusN2Collecteurs} × ${quest.bonusN2Multiplicateur}`);

      if (zoneTexte)
        metaLine("Zone géographique", zoneTexte);

      // La mention « protocole Tribe Africa v1.0 » a été retirée : aucun
      // protocole de ce nom n'existe (Z6-11).

      ink(60, 60, 80); doc.setFontSize(8); doc.setFont("helvetica", "normal");
      doc.text(`Tribe Africa · Rapport de quête · ${today}`, W / 2, H - 8, { align: "center" });
      fill(124, 58, 237); rect(0, H - 2, W, 2);

      /* ── Sauvegarde ── */
      const slug = quest.titre.replace(/[^a-zA-Z0-9]/g, "-").replace(/-+/g, "-").toLowerCase();
      const dateStr = new Date().toISOString().slice(0, 10);
      doc.save(`rapport-${slug}-${dateStr}.pdf`);
      noterActivite("export", EXPORTS.queteRapportPdf);

    } catch (err: any) {
      dispatch(openSnackbar(`Erreur génération PDF : ${err.message}`, "red"));
    } finally {
      setGenerating(false);
      dispatch(loaderOff());
    }
  };

  /* Devis, contrats, factures et avoirs : produits et archivés par le serveur
     (admin-service), émis et téléchargés depuis le bloc Facturation. Le
     navigateur ne dessine plus aucun document fiscal. */

  /* ── Paiement : marquer devis envoyé ── */
  const handleMarquerDevisEnvoye = async () => {
    if (!quest || !id) return;
    setUpdatingPaiement(true);
    dispatch(loaderOn());
    try {
      // Route dédiée : transition contrôlée par le serveur, tracée, email au client (Z6-13).
      // Transition refusée (retour en arrière, D10) : le motif du serveur est affiché.
      await locationApi.patch(`/quest/${id}/paiement`, { statutPaiement: "devis_envoye" });
      dispatch(openSnackbar("Statut paiement mis à jour : Devis envoyé", "green"));
      loadQuest();
    } catch (err: any) {
      dispatch(openSnackbar(messageServeur(err, "Statut de paiement refusé."), "red"));
    } finally {
      setUpdatingPaiement(false);
      dispatch(loaderOff());
    }
  };

  /* ════════════════════════ RENDER */
  if (loading) {
    return (
      <Box sx={{ background: C.bg, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress sx={{ color: C.accentSoft }} />
      </Box>
    );
  }

  if (!quest) {
    return (
      <Box sx={{ background: C.bg, minHeight: "100vh", color: C.text, p: 3, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2 }}>
        <Typography sx={{ fontSize: "16px", fontWeight: 700 }}>Quête introuvable</Typography>
        <Button onClick={() => navigate("/quetes/liste")} sx={{ color: C.accentSoft, textTransform: "none" }}>
          Retour à la liste
        </Button>
      </Box>
    );
  }

  /* Gamification shortcuts */
  const tokensBase = quest.tokensBase ?? quest.recompense ?? null;
  const hasBonus   = quest.bonusN1Collecteurs != null && quest.bonusN1Multiplicateur != null;

  return (
    <Box sx={{ background: C.bg, minHeight: "100vh", color: C.text, p: { xs: 2, md: 3 } }}>

      {/* ══ HEADER ══ */}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 3, gap: 2, flexWrap: "wrap" }}>

        <Box>
          <Button
            startIcon={<ArrowBackRoundedIcon sx={{ fontSize: "14px !important" }} />}
            onClick={() => navigate("/quetes/liste")}
            sx={{
              color: C.sub, fontSize: "12px", fontWeight: 600, textTransform: "none",
              borderRadius: "6px", px: 1, py: "4px", mb: 1, minWidth: 0,
              "&:hover": { background: "rgba(31,30,27,0.05)", color: C.text },
            }}
          >
            Retour aux quêtes
          </Button>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mb: 0.75 }}>
            {quest.logoUrl && (
              <Box
                component="img" src={quest.logoUrl} alt="logo"
                sx={{ width: 32, height: 32, borderRadius: "7px", objectFit: "cover", border: `1px solid ${C.border}` }}
              />
            )}
            <Typography sx={{ fontSize: "22px", fontWeight: 800, letterSpacing: "-0.02em", color: C.text, lineHeight: 1.2 }}>
              {quest.titre}
            </Typography>
            <BoutonAide texte={AIDE_FICHE_QUETE.page} />
            {qst && <StatusBadge cfg={qst} />}
            <Box sx={{
              display: "inline-flex", alignItems: "center", gap: "5px",
              background: quest.validationAuto ? "rgba(53,106,15,0.09)" : "rgba(31,30,27,0.05)",
              border: `1px solid ${quest.validationAuto ? "rgba(53,106,15,0.28)" : "rgba(31,30,27,0.12)"}`,
              borderRadius: "5px", px: "8px", py: "3px",
            }}>
              <Box sx={{ width: 5, height: 5, borderRadius: "50%", flexShrink: 0,
                background: quest.validationAuto ? "#356A0F" : "rgba(31,30,27,0.35)" }} />
              <Typography sx={{ fontSize: "11px", fontWeight: 600, lineHeight: 1,
                color: quest.validationAuto ? "#356A0F" : "rgba(31,30,27,0.50)" }}>
                {quest.validationAuto ? "Validation auto" : "Validation manuelle"}
              </Typography>
            </Box>
            {quest.cycleActif && (
              <Box sx={{
                display: "inline-flex", alignItems: "center", gap: "5px",
                background: C.accentBg, border: `1px solid ${C.accentBorder}`,
                borderRadius: "5px", px: "8px", py: "3px",
              }}>
                <Box sx={{ width: 5, height: 5, borderRadius: "50%", flexShrink: 0, background: C.accentSoft }} />
                <Typography sx={{ fontSize: "11px", fontWeight: 600, lineHeight: 1, color: C.accentSoft }}>
                  Mission récurrente
                </Typography>
              </Box>
            )}
            {quest.parentQuestId && (
              <Box sx={{
                display: "inline-flex", alignItems: "center", gap: "5px",
                background: C.accentBg, border: `1px solid ${C.accentBorder}`,
                borderRadius: "5px", px: "8px", py: "3px",
              }}>
                <Typography sx={{ fontSize: "11px", fontWeight: 600, lineHeight: 1, color: C.accentSoft }}>
                  Cycle {quest.cycleNumero ?? ""}
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
            {(quest.types ?? []).map(t => (
              <Chip key={t}
                label={`${QUEST_TYPE_EMOJIS[t] ?? ""} ${QUEST_TYPE_LABELS[t] ?? t}`}
                size="small"
                sx={{
                  height: 20, fontSize: "10px", fontWeight: 600,
                  background: C.accentBg, color: C.accentSoft,
                  border: `1px solid ${C.accentBorder}`, borderRadius: "5px",
                  "& .MuiChip-label": { px: "6px" },
                }}
              />
            ))}
            {quest.dateFin && (
              <Typography sx={{ fontSize: "11px", color: C.sub }}>
                · Expire le {fmtDate(quest.dateFin)}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Actions */}
        <Box sx={{ display: "flex", gap: 1, flexShrink: 0, alignItems: "flex-start", mt: "2px", flexWrap: "wrap" }}>
          {/* Modifier */}
          <Button
            startIcon={<EditRoundedIcon sx={{ fontSize: "13px !important" }} />}
            onClick={handleEdit}
            sx={{
              background: C.accentBg, color: C.accentSoft, border: `1px solid ${C.accentBorder}`,
              fontSize: "12px", fontWeight: 700, textTransform: "none", borderRadius: "7px", px: 2, py: "8px",
              "&:hover": { background: "rgba(31,30,27,0.18)", borderColor: C.accentSoft },
            }}
          >
            Modifier
          </Button>

          {/* Publier / Dépublier */}
          {isPublished ? (
            <Button
              startIcon={unpublishing
                ? <CircularProgress size={12} sx={{ color: C.gold }} />
                : <UnpublishedRoundedIcon sx={{ fontSize: "13px !important" }} />}
              disabled={unpublishing}
              onClick={doUnpublish}
              sx={{
                background: C.goldBg, color: C.gold, border: `1px solid ${C.goldBdr}`,
                fontSize: "12px", fontWeight: 700, textTransform: "none", borderRadius: "7px", px: 2, py: "8px",
                "&:hover": { background: "rgba(124,86,0,0.18)", borderColor: C.gold },
                "&.Mui-disabled": { color: C.gold, opacity: 0.5 },
              }}
            >
              {unpublishing ? "…" : "Dépublier"}
            </Button>
          ) : !archivee && (
            <Button
              startIcon={<PublishRoundedIcon sx={{ fontSize: "13px !important" }} />}
              onClick={handlePublish}
              sx={{
                background: C.successBg, color: C.success, border: `1px solid ${C.successBdr}`,
                fontSize: "12px", fontWeight: 700, textTransform: "none", borderRadius: "7px", px: 2, py: "8px",
                "&:hover": { background: "rgba(53,106,15,0.16)", borderColor: C.success },
              }}
            >
              Valider et publier
            </Button>
          )}

          {/* Clôturer (Q9) */}
          {!archivee && (
            <Button
              startIcon={cloturant
                ? <CircularProgress size={12} sx={{ color: C.label }} />
                : <ArchiveOutlinedIcon sx={{ fontSize: "13px !important" }} />}
              disabled={cloturant}
              onClick={handleCloturer}
              sx={{
                background: "rgba(31,30,27,0.04)", color: C.label, border: `1px solid ${C.border}`,
                fontSize: "12px", fontWeight: 700, textTransform: "none", borderRadius: "7px", px: 2, py: "8px",
                "&:hover": { background: "rgba(31,30,27,0.08)", borderColor: C.label },
                "&.Mui-disabled": { color: C.label, opacity: 0.5 },
              }}
            >
              {cloturant ? "…" : "Clôturer"}
            </Button>
          )}

          {/* Générer le rapport */}
          <Button
            startIcon={generating
              ? <CircularProgress size={12} sx={{ color: '#fff' }} />
              : <span style={{ fontSize: 14 }}>📊</span>}
            disabled={generating}
            onClick={generatePDF}
            sx={{
              background: 'transparent', color: C.text,
              border: '1px solid rgba(31,30,27,0.2)',
              fontSize: "12px", fontWeight: 700, textTransform: "none", borderRadius: "7px", px: 2, py: "8px",
              "&:hover": { background: 'rgba(31,30,27,0.15)', borderColor: 'rgba(31,30,27,0.5)' },
              "&.Mui-disabled": { color: C.label, opacity: 0.6 },
            }}
          >
            {generating ? "Génération en cours..." : "Générer le rapport"}
          </Button>

          {/* Exporter CSV */}
          <Button
            startIcon={exportant
              ? <CircularProgress size={12} sx={{ color: '#fff' }} />
              : <span style={{ fontSize: 14 }}>⬇</span>}
            disabled={exportant}
            onClick={exporterCsv}
            sx={{
              background: 'transparent', color: C.text,
              border: '1px solid rgba(31,30,27,0.2)',
              fontSize: "12px", fontWeight: 700, textTransform: "none", borderRadius: "7px", px: 2, py: "8px",
              "&:hover": { background: 'rgba(31,30,27,0.15)', borderColor: 'rgba(31,30,27,0.5)' },
              "&.Mui-disabled": { color: C.label, opacity: 0.6 },
            }}
          >
            {exportant ? "Export…" : "Exporter CSV"}
          </Button>

          {/* Supprimer */}
          <Button
            startIcon={deleting
              ? <CircularProgress size={12} sx={{ color: C.error }} />
              : <DeleteOutlineRoundedIcon sx={{ fontSize: "13px !important" }} />}
            disabled={deleting}
            onClick={handleDelete}
            sx={{
              background: C.errorBg, color: C.error, border: `1px solid ${C.errorBdr}`,
              fontSize: "12px", fontWeight: 700, textTransform: "none", borderRadius: "7px", px: 2, py: "8px",
              "&:hover": { background: "rgba(162,58,13,0.16)", borderColor: C.error },
              "&.Mui-disabled": { color: C.error, opacity: 0.5 },
            }}
          >
            {deleting ? "…" : "Supprimer"}
          </Button>
        </Box>
      </Box>

      {/* ══ CONTENU DE LA QUÊTE ══ */}
      <Box sx={{ background: C.card, borderRadius: "8px", border: "1.5px solid #1F1E1B", p: 2.5, mb: 2 }}>
        <Typography sx={{ fontSize: "13px", fontWeight: 700, color: C.text, mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
          Contenu de la quête
          <BoutonAide texte={AIDE_FICHE_QUETE.contenu} />
          <Box component="span" sx={{ fontSize: "10px", fontWeight: 600, color: C.label, background: "rgba(31,30,27,0.05)", border: "1.5px solid #1F1E1B", borderRadius: "4px", px: "6px", py: "2px" }}>
            Lecture seule
          </Box>
        </Typography>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3 }}>

          {/* ── Informations générales ── */}
          <Box>
            <SectionTitle>Informations générales</SectionTitle>
            <Box sx={{ background: C.cardInner, borderRadius: "8px", border: `1px solid ${C.border}`, p: 1.5 }}>
              {quest.logoUrl && (
                <InfoRow label="Logo">
                  <Box component="img" src={quest.logoUrl} alt="logo"
                    sx={{ height: 36, borderRadius: "6px", objectFit: "contain", border: `1px solid ${C.border}` }} />
                </InfoRow>
              )}
              {quest.description && (
                <InfoRow label="Description">
                  <Typography sx={{ fontSize: "13px", color: C.text, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
                    {quest.description}
                  </Typography>
                </InfoRow>
              )}
              {quest.frequence && (
                <InfoRow label="Fréquence">
                  <Typography sx={{ fontSize: "13px", color: C.text }}>{quest.frequence}</Typography>
                </InfoRow>
              )}
              <InfoRow label="Date début">
                <Typography sx={{ fontSize: "13px", color: C.text }}>{fmtDate(quest.dateDebut)}</Typography>
              </InfoRow>
              <InfoRow label="Date fin">
                <Typography sx={{ fontSize: "13px", color: C.text }}>{fmtDate(quest.dateFin)}</Typography>
              </InfoRow>
              {attendues > 0 && (
                <InfoRow label="Collectes attendues">
                  <Typography sx={{ fontSize: "13px", color: C.text }}>{attendues}</Typography>
                </InfoRow>
              )}
            </Box>
          </Box>

          {/* ── Types de collecte ── */}
          <Box>
            <SectionTitle>Types de collecte</SectionTitle>
            <Box sx={{ background: C.cardInner, borderRadius: "8px", border: `1px solid ${C.border}`, p: 1.5, display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {(quest.types ?? []).length === 0 && (
                <Typography sx={{ fontSize: "12px", color: C.muted }}>—</Typography>
              )}
              {(quest.types ?? []).map(t => (
                <Box key={t} sx={{
                  display: "flex", alignItems: "center", gap: "6px",
                  background: C.accentBg, border: `1px solid ${C.accentBorder}`,
                  borderRadius: "8px", px: "10px", py: "6px",
                }}>
                  <Typography sx={{ fontSize: "16px", lineHeight: 1 }}>{QUEST_TYPE_EMOJIS[t] ?? "🔹"}</Typography>
                  <Typography sx={{ fontSize: "12px", fontWeight: 600, color: C.accentSoft }}>
                    {QUEST_TYPE_LABELS[t] ?? t}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* ── Champs de collecte ── */}
            {(quest.champs ?? []).length > 0 && (
              <Box sx={{ mt: 2 }}>
                <SectionTitle>Champs de collecte</SectionTitle>
                <Box sx={{ background: C.cardInner, borderRadius: "8px", border: `1px solid ${C.border}`, overflow: "hidden" }}>
                  {(quest.champs ?? []).map((champ, i) => {
                    const tc  = fieldTypeStyle(champ.type);
                    const req = champ.obligatoire === true || champ.obligatoire === "true";
                    const options: string[] = champ.type === "choix" && champ.optionsJson
                      ? champ.optionsJson.split(",").map(s => s.trim()).filter(Boolean)
                      : [];

                    return (
                      <Box key={i} sx={{
                        display: "flex", alignItems: "flex-start", gap: 1.5, px: 1.5, py: "10px",
                        borderBottom: i < (quest.champs ?? []).length - 1 ? `1px solid ${C.border}` : "none",
                        background: i % 2 === 1 ? "rgba(31,30,27,0.012)" : "transparent",
                      }}>
                        {/* Type badge */}
                        <Box sx={{
                          flexShrink: 0, background: tc.bg, border: `1px solid ${tc.border}`,
                          borderRadius: "4px", px: "6px", py: "2px", mt: "1px",
                        }}>
                          <Typography sx={{ fontSize: "10px", fontWeight: 700, color: tc.color, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                            {champ.type ?? "?"}
                          </Typography>
                        </Box>

                        {/* Label + options */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <Typography sx={{ fontSize: "12px", fontWeight: 600, color: C.text }}>
                              {champ.label || "—"}
                            </Typography>
                            {req && (
                              <Tooltip title="Champ obligatoire" arrow>
                                <LockOutlinedIcon sx={{ fontSize: 12, color: C.gold }} />
                              </Tooltip>
                            )}
                          </Box>
                          {options.length > 0 && (
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px", mt: "5px" }}>
                              {options.map(opt => (
                                <Box key={opt} sx={{
                                  display: "flex", alignItems: "center", gap: "4px",
                                  background: "rgba(31,30,27,0.05)", border: `1px solid ${C.borderInput}`,
                                  borderRadius: "4px", px: "6px", py: "2px",
                                }}>
                                  <Box sx={{ width: 4, height: 4, borderRadius: "50%", background: C.accentSoft, flexShrink: 0 }} />
                                  <Typography sx={{ fontSize: "11px", color: C.sub }}>{opt}</Typography>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* ══ ZONE GÉOGRAPHIQUE ══
          Le bloc était conditionné par hasZone : sur une quête sans bbox il
          disparaissait entièrement, et rien ne signalait à l'admin qu'une
          géographie manquait. Il est désormais toujours rendu, avec trois
          états. ══ */}
      <Box sx={{ background: C.card, borderRadius: "8px", border: "1.5px solid #1F1E1B", p: 2.5, mb: 2 }}>
        <Typography component="div" sx={{ fontSize: "13px", fontWeight: 700, color: C.text, mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
          Zone géographique
          <BoutonAide texte={AIDE_FICHE_QUETE.zone} />
        </Typography>

        {!hasZone && (
          quest.zoneDemandee ? (
            /* Quête du portail IA dont le nom de zone n'a pas pu être résolu. */
            <Box sx={{
              background: "rgba(124,86,0,0.08)", border: "1px solid rgba(124,86,0,0.25)",
              borderRadius: "8px", px: 2, py: "12px",
              display: "flex", alignItems: "flex-start", gap: 1.5,
            }}>
              <Typography sx={{ fontSize: "16px", lineHeight: 1, mt: "1px", flexShrink: 0 }}>📍</Typography>
              <Box>
                <Typography sx={{ fontSize: "12px", color: "#7C5600", fontWeight: 700, mb: "3px" }}>
                  Zone demandée par le client : « {quest.zoneDemandee} »
                </Typography>
                <Typography sx={{ fontSize: "11px", color: C.sub, lineHeight: 1.6 }}>
                  La résolution automatique n'a rien trouvé pour ce nom dans la région de
                  Dakar. La quête n'a pas de géographie : ouvrez l'édition pour la tracer.
                </Typography>
              </Box>
            </Box>
          ) : (
            /* Quête créée depuis le back-office sans zone : rien n'a été demandé. */
            <Box sx={{
              background: C.cardInner, border: `1px solid ${C.border}`,
              borderRadius: "8px", px: 2, py: "12px",
            }}>
              <Typography sx={{ fontSize: "12px", color: C.sub, lineHeight: 1.6 }}>
                Aucune zone définie. Le géorepérage des soumissions est inactif tant
                qu'aucune zone n'est tracée — ouvrez l'édition pour en définir une.
              </Typography>
            </Box>
          )
        )}

        {hasZone && (<>
          {!aUnRectangle(quest) ? (
            <Typography sx={{ fontSize: "12px", color: C.sub, mb: 1.5 }}>
              Polygone de {polygoneDe(quest).length} sommets
            </Typography>
          ) : (
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 1.5 }}>
            {[
              { label: "Lat. min", value: quest.latMin?.toFixed(5) },
              { label: "Lat. max", value: quest.latMax?.toFixed(5) },
              { label: "Lng. min", value: quest.lngMin?.toFixed(5) },
              { label: "Lng. max", value: quest.lngMax?.toFixed(5) },
            ].map(({ label, value }) => (
              <Box key={label} sx={{
                background: C.cardInner, border: `1px solid ${C.border}`,
                borderRadius: "6px", px: "10px", py: "6px",
              }}>
                <Typography sx={{ fontSize: "10px", color: C.label, textTransform: "uppercase", letterSpacing: "0.08em" }}>{label}</Typography>
                <Typography sx={{ fontSize: "12px", fontWeight: 600, color: C.text, fontFamily: "monospace" }}>{value}</Typography>
              </Box>
            ))}
          </Box>
          )}
          {/* Mini-map */}
          <Box
            ref={zoneContainerRef}
            sx={{
              height: 160, borderRadius: "8px",
              border: `1px solid ${C.border}`, overflow: "hidden",
              background: "#111",
            }}
          />
        </>)}
      </Box>

      {/* ══ TIPS & BONUS ══ */}
      {tokensBase != null && (
        <Box sx={{ background: C.card, borderRadius: "8px", border: "1.5px solid #1F1E1B", p: 2.5, mb: 2 }}>
          <Typography component="div" sx={{ fontSize: "13px", fontWeight: 700, color: C.text, mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
            Tips & système de réputation
            <BoutonAide texte={AIDE_FICHE_QUETE.tipsReputation} />
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {/* Base */}
            <Box sx={{
              display: "flex", alignItems: "center", gap: 1.5,
              background: C.cardInner, border: `1px solid ${C.border}`,
              borderRadius: "8px", px: 1.5, py: "10px",
            }}>
              <Typography sx={{ fontSize: "18px", lineHeight: 1 }}>🎯</Typography>
              <Box>
                <Typography sx={{ fontSize: "11px", color: C.label, textTransform: "uppercase", letterSpacing: "0.08em" }}>Tips de base</Typography>
                <Typography sx={{ fontSize: "20px", fontWeight: 800, color: C.gold, lineHeight: 1.1 }}>
                  {tokensBase}
                  <Typography component="span" sx={{ fontSize: "12px", fontWeight: 500, color: C.sub, ml: "4px" }}>Tips / collecte</Typography>
                </Typography>
              </Box>
            </Box>

            {hasBonus && (() => {
              const n1  = quest.bonusN1Collecteurs!;
              const m1  = quest.bonusN1Multiplicateur!;
              const n2  = quest.bonusN2Collecteurs;
              const m2  = quest.bonusN2Multiplicateur;
              return (
                <>
                  {/* Tier 1 */}
                  <Box sx={{
                    display: "flex", alignItems: "center", gap: 1.5,
                    background: "rgba(124,86,0,0.07)", border: `1px solid ${C.goldBdr}`,
                    borderRadius: "8px", px: 1.5, py: "10px",
                  }}>
                    <Typography sx={{ fontSize: "18px", lineHeight: 1 }}>🥇</Typography>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: "11px", color: C.label, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                        Top {n1} premier{n1 > 1 ? "s" : ""}
                      </Typography>
                      <Typography sx={{ fontSize: "13px", color: C.text }}>
                        ×{m1}{" "}
                        <Typography component="span" sx={{ color: C.sub, fontSize: "12px" }}>→</Typography>
                        {" "}<Typography component="span" sx={{ fontWeight: 800, color: C.gold }}>
                          {Math.round(tokensBase * m1)} Tips
                        </Typography>
                      </Typography>
                    </Box>
                  </Box>

                  {/* Tier 2 */}
                  {n2 != null && m2 != null && (
                    <Box sx={{
                      display: "flex", alignItems: "center", gap: 1.5,
                      background: "rgba(148,163,184,0.07)", border: "1px solid rgba(148,163,184,0.22)",
                      borderRadius: "8px", px: 1.5, py: "10px",
                    }}>
                      <Typography sx={{ fontSize: "18px", lineHeight: 1 }}>🥈</Typography>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: "11px", color: C.label, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                          Du rang {n1 + 1} à {n2}
                        </Typography>
                        <Typography sx={{ fontSize: "13px", color: C.text }}>
                          ×{m2}{" "}
                          <Typography component="span" sx={{ color: C.sub, fontSize: "12px" }}>→</Typography>
                          {" "}<Typography component="span" sx={{ fontWeight: 800, color: "#57534A" }}>
                            {Math.round(tokensBase * m2)} Tips
                          </Typography>
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Reste */}
                  <Box sx={{
                    display: "flex", alignItems: "center", gap: 1.5,
                    background: "rgba(31,30,27,0.03)", border: `1px solid ${C.borderInput}`,
                    borderRadius: "8px", px: 1.5, py: "10px",
                  }}>
                    <Typography sx={{ fontSize: "18px", lineHeight: 1 }}>👤</Typography>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: "11px", color: C.label, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                        Autres Spotters
                      </Typography>
                      <Typography sx={{ fontSize: "13px", color: C.text }}>
                        Tips de base{" "}
                        <Typography component="span" sx={{ color: C.sub, fontSize: "12px" }}>→</Typography>
                        {" "}<Typography component="span" sx={{ fontWeight: 800, color: C.sub }}>
                          {tokensBase} Tips
                        </Typography>
                      </Typography>
                    </Box>
                  </Box>
                </>
              );
            })()}
          </Box>
        </Box>
      )}

      {/* ══ MODE CHALLENGE ══ */}
      {quest.challengeMode === true && (
        <Box sx={{
          background: "rgba(31,30,27,0.08)",
          border: "1px solid rgba(31,30,27,0.25)",
          borderRadius: "8px", p: 2.5, mb: 2,
        }}>
          {/* Header */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Typography component="div" sx={{ fontSize: "14px", fontWeight: 700, color: "#1F1E1B", display: "flex", alignItems: "center", gap: 1 }}>
              🏆 Mode Challenge
              <BoutonAide texte={AIDE_FICHE_QUETE.gagnant} />
            </Typography>
            {!quest.challengeGagnantSoumissionId && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => setWinnerDialogOpen(true)}
              sx={{
                color: C.accentSoft,
                borderColor: "rgba(31,30,27,0.5)",
                fontSize: "12px", fontWeight: 700, textTransform: "none",
                borderRadius: "7px", px: 2, py: "6px",
                "&:hover": { background: "rgba(31,30,27,0.12)", borderColor: C.accentSoft },
              }}
            >
              Désigner le gagnant
            </Button>
            )}
          </Box>

          {/* Challenge fields */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <InfoRow label="Récompense du gagnant">
              <Typography sx={{ fontSize: "13px", color: C.text, fontWeight: 600 }}>
                {(() => {
                  const tips = Number(quest.challengeRecompenseTips) || 0;
                  const cfa = enCfa(tips, valeurTip);
                  return tips > 0
                    ? `${tips.toLocaleString("fr-FR")} Tips${cfa != null ? ` (≈ ${cfa.toLocaleString("fr-FR")} F CFA)` : ""}`
                    : "Aucune récompense en Tips";
                })()}
              </Typography>
            </InfoRow>
            {quest.challengeRegleVictoire && (
              <InfoRow label="Règle de victoire">
                <Typography sx={{ fontSize: "13px", color: C.text }}>{libelleRegleVictoire(quest.challengeRegleVictoire)}</Typography>
              </InfoRow>
            )}
            {quest.challengeGagnantSoumissionId && (
              <InfoRow label="Gagnant">
                <Typography sx={{ fontSize: "13px", color: C.success, fontWeight: 600 }}>
                  {masquerEmail(quest.challengeGagnantEmail)}
                  <span style={{ color: C.sub, fontWeight: 400 }}>
                    {quest.challengeGagnantLe ? ` · le ${fmtDateTime(quest.challengeGagnantLe)}` : ""}
                    {quest.challengeGagnantPar ? ` · par ${quest.challengeGagnantPar}` : ""}
                  </span>
                </Typography>
                {quest.challengeCreditEchec && (
                  <Box sx={{ mt: 0.5 }}>
                    <RelanceCredit type="challenge" id={quest.id || id || ""}
                      message={quest.challengeCreditEchecMessage} date={quest.challengeCreditEchecLe}
                      onRelance={() => refreshAll()} />
                  </Box>
                )}
              </InfoRow>
            )}
          </Box>
        </Box>
      )}

      {/* ══ KPI CARDS ══ */}
      <Box sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "wrap" }}>
        <KpiCard label="Soumissions totales"
          value={statsLoading ? <CircularProgress size={18} sx={{ color: C.accentSoft }} /> : total}
          color={C.text} />
        <KpiCard label="Spotters actifs"
          value={statsLoading ? <CircularProgress size={18} sx={{ color: C.accentSoft }} /> : collecteurs}
          color={C.accentSoft} />
        <KpiCard label="Tips distribués"
          value={statsLoading ? <CircularProgress size={18} sx={{ color: C.accentSoft }} /> : tokens.toLocaleString("fr-FR")}
          color={C.gold}
          sub={tokensBase ? `${tokensBase} Tips / collecte` : undefined} />
        <KpiCard label="Taux de validation"
          value={statsLoading ? <CircularProgress size={18} sx={{ color: C.accentSoft }} /> : `${tauxValidation}%`}
          color={tauxValidation >= 80 ? C.success : tauxValidation >= 50 ? C.gold : C.error}
          sub={total > 0 ? `${valides} validée${valides > 1 ? "s" : ""} sur ${total}` : undefined} />
      </Box>

      {/* ══ PROGRESS BAR ══ */}
      {attendues > 0 && (
        <Box sx={{ background: C.card, borderRadius: "8px", border: "1.5px solid #1F1E1B", p: 2, mb: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography sx={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: C.label }}>
                Progression des collectes
              </Typography>
              <BoutonAide texte={AIDE_FICHE_QUETE.progression} />
            </Box>
            <Typography sx={{ fontSize: "13px", fontWeight: 700, color: progressPct >= 100 ? C.success : C.text }}>
              {realises} / {attendues}
              <Typography component="span" sx={{ fontSize: "11px", color: C.sub, ml: "4px" }}>({progressPct}%)</Typography>
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={progressPct} sx={{
            height: 6, borderRadius: 2, backgroundColor: "rgba(31,30,27,0.07)",
            "& .MuiLinearProgress-bar": {
              borderRadius: 2,
              background: progressPct >= 100
                ? C.success
                : C.accent,
            },
          }} />
          {realises < attendues && (
            <Typography sx={{ fontSize: "11px", color: C.sub, mt: "6px" }}>
              Encore {attendues - realises} collecte{attendues - realises > 1 ? "s" : ""} attendue{attendues - realises > 1 ? "s" : ""}
            </Typography>
          )}
        </Box>
      )}

      {/* ══ CARTE DES COLLECTES EN TEMPS RÉEL ══ */}
      {(() => {
        const valides   = mapSubs.filter(s => s.statut === "valide").length;
        const attente   = mapSubs.filter(s => s.statut === "en_attente" || !s.statut).length;
        const rejetes   = mapSubs.filter(s => s.statut === "rejete").length;
        const expirees  = mapSubs.filter(s => s.statut === "expire").length;
        const secLabel  = secSince < 60 ? `${secSince}s` : `${Math.floor(secSince / 60)}min`;
        return (
          <Box sx={{ background: C.card, borderRadius: "8px", border: "1.5px solid #1F1E1B", overflow: "hidden", mb: 2 }}>
            {/* Header + compteur */}
            <Box sx={{ px: 2.5, py: "12px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: C.success, flexShrink: 0,
                  animation: "pulse 2s infinite", "@keyframes pulse": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.4 } } }} />
                <Typography sx={{ fontSize: "13px", fontWeight: 700, color: C.text }}>Carte des collectes en temps réel</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                {[
                  { label: `${valides} validée${valides > 1 ? "s" : ""}`, color: C.success, bg: C.successBg, bdr: C.successBdr },
                  { label: `${attente} en attente`,                        color: C.gold,    bg: C.goldBg,    bdr: C.goldBdr   },
                  { label: `${rejetes} rejetée${rejetes > 1 ? "s" : ""}`, color: C.error,   bg: C.errorBg,   bdr: C.errorBdr  },
                  ...(expirees > 0
                    ? [{ label: `${expirees} expirée${expirees > 1 ? "s" : ""}`, color: C.sub, bg: "rgba(31,30,27,0.04)", bdr: C.border }]
                    : []),
                ].map(b => (
                  <Box key={b.label} sx={{ display: "inline-flex", alignItems: "center", gap: "4px", background: b.bg, border: `1px solid ${b.bdr}`, borderRadius: "5px", px: "7px", py: "2px" }}>
                    <Box sx={{ width: 5, height: 5, borderRadius: "50%", background: b.color, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: "10px", fontWeight: 700, color: b.color, lineHeight: 1 }}>{b.label}</Typography>
                  </Box>
                ))}
                {lastRefreshAt && (
                  <Typography sx={{ fontSize: "10px", color: C.muted }}>Mis à jour il y a {secLabel}</Typography>
                )}
              </Box>
            </Box>

            {/* Badge GPS suspect */}
            {(() => {
              const withCoords = mapSubs.filter(s => s.latitude != null && s.longitude != null);
              if (withCoords.length < 4) return null;
              const freq: Record<string, number> = {};
              withCoords.forEach(s => { const k = `${s.latitude},${s.longitude}`; freq[k] = (freq[k] ?? 0) + 1; });
              const maxSame = Math.max(...Object.values(freq));
              if (maxSame <= 3) return null;
              return (
                <Box sx={{ mx: 2, mb: 1.5, display: "inline-flex", alignItems: "center", gap: "6px", background: C.goldBg, border: `1px solid ${C.goldBdr}`, borderRadius: "6px", px: 2, py: "7px" }}>
                  <Typography sx={{ fontSize: "11px", fontWeight: 700, color: C.gold }}>
                    ⚠ Collectes suspectes — GPS identique détecté ({maxSame} points superposés)
                  </Typography>
                </Box>
              );
            })()}

            {/* Map */}
            <Box sx={{ position: "relative", height: 420 }}>
              {mapLoading && (
                <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5, zIndex: 10, background: C.card }}>
                  <CircularProgress size={20} sx={{ color: C.accentSoft }} />
                  <Typography sx={{ fontSize: "12px", color: C.sub }}>Chargement de la carte…</Typography>
                </Box>
              )}
              <div ref={liveMapContainerRef} style={{ width: "100%", height: "100%" }} />
            </Box>
          </Box>
        );
      })()}

      {/* ══ FACTURATION ══ */}
      {(() => {
        const sp = quest.statutPaiement;
        const { libelle, ton } = statutPaiement(sp, "En attente de devis");
        const badge = { label: libelle, ...COULEURS[ton] };
        return (
          <Box sx={{ background: "var(--c-surface)", border: "1px solid rgba(31,30,27,0.06)", borderRadius: "8px", p: 2.5, mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
              <Typography sx={{ fontSize: "13px", fontWeight: 700, color: C.text }}>Facturation</Typography>
              <BoutonAide texte={AIDE_FICHE_QUETE.facturation} />
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: "5px", background: badge.bg, border: `1px solid ${badge.border}`, borderRadius: "5px", px: "8px", py: "3px" }}>
                <Box sx={{ width: 5, height: 5, borderRadius: "50%", background: badge.color, flexShrink: 0 }} />
                <Typography sx={{ fontSize: "11px", fontWeight: 600, color: badge.color, lineHeight: 1 }}>{badge.label}</Typography>
              </Box>
            </Box>
            <BlocFacturation questId={quest.id || id || ""} titre={quest.titre} budgetClient={quest.budgetClient}
              onChange={loadQuest}
              actionsEnTete={!sp ? (
                /* Étape saisie avant facturation ; « payé » se déduit des encaissements. */
                <Button
                  disabled={updatingPaiement}
                  onClick={handleMarquerDevisEnvoye}
                  sx={{
                    background: C.goldBg, color: C.gold, border: `1px solid ${C.goldBdr}`,
                    fontSize: "12px", fontWeight: 700, textTransform: "none", borderRadius: "7px", px: 2, py: "6px",
                    "&:hover": { background: "rgba(124,86,0,0.18)", borderColor: C.gold },
                    "&.Mui-disabled": { color: C.gold, opacity: 0.5 },
                  }}
                >
                  Marquer comme devis envoyé
                </Button>
              ) : undefined} />
          </Box>
        );
      })()}

      {/* ══ SUBMISSIONS TABLE ══ */}
      <Box sx={{ background: C.card, borderRadius: "8px", border: "1.5px solid #1F1E1B", overflow: "hidden" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2.5, pt: 2, pb: 1 }}>
          <Typography sx={{ fontSize: "13px", fontWeight: 700, color: C.text }}>Collectes reçues</Typography>
          <BoutonAide texte={AIDE_FICHE_QUETE.collectes} />
        </Box>
        <Box sx={{
          display: "grid", gridTemplateColumns: "44px 1fr 160px 60px 110px 180px",
          px: 2.5, py: "10px",
          borderBottom: `1px solid ${C.border}`,
          background: "rgba(31,30,27,0.02)", alignItems: "center",
        }}>
          {["#", "Spotter", "Date-heure", "Photo", "Statut", ""].map((h, i) => (
            <Typography key={i} sx={{
              fontSize: "10px", fontWeight: 700, letterSpacing: "0.10em",
              textTransform: "uppercase", color: C.label,
              textAlign: i === 5 ? "right" : "left",
            }}>{h}</Typography>
          ))}
        </Box>

        {subLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 5, gap: 1.5 }}>
            <CircularProgress size={18} sx={{ color: C.accentSoft }} />
            <Typography sx={{ fontSize: "13px", color: C.sub }}>Chargement des soumissions…</Typography>
          </Box>
        )}

        {!subLoading && submissions.length === 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 6, gap: 1 }}>
            <Typography sx={{ fontSize: "14px", fontWeight: 600, color: C.text }}>Aucune soumission</Typography>
            <Typography sx={{ fontSize: "12px", color: C.sub }}>Les soumissions des Spotters apparaîtront ici.</Typography>
          </Box>
        )}

        {!subLoading && submissions.map((sub, i) => {
          const sid      = getId(sub);
          const isAct    = actionId === sid;
          const stCfg    = submissionStatusStyle(sub.statut);
          const isPending = (sub.statut?.toLowerCase() ?? "en_attente") === "en_attente";
          const photoUrl  = sub.photoUrl;

          return (
            <Box key={sid || i} sx={{
              display: "grid", gridTemplateColumns: "44px 1fr 160px 60px 110px 180px",
              px: 2.5, py: "11px",
              borderBottom: i < submissions.length - 1 ? `1px solid ${C.border}` : "none",
              background: i % 2 === 1 ? C.row : "transparent",
              alignItems: "center", transition: "background 0.10s ease",
              "&:hover": { background: C.rowHover },
            }}>
              <Typography sx={{ fontSize: "12px", color: C.muted, fontFamily: "monospace" }}>{i + 1}</Typography>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                <Box sx={{
                  width: 28, height: 28, borderRadius: "7px", flexShrink: 0,
                  background: C.accentBg, border: `1px solid ${C.accentBorder}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Typography sx={{ fontSize: "10px", fontWeight: 800, color: C.accentSoft }}>
                    {initials(sub.collecteurEmail)}
                  </Typography>
                </Box>
                <Typography sx={{
                  fontSize: "12px", color: C.text,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {sub.collecteurEmail ?? sub.collecteurNom ?? "—"}
                </Typography>
                {/* Vision : verdict + EXIF (aide à la décision, aucun paiement) */}
                {sub.verdictVision?.verdict && (() => {
                  const v = sub.verdictVision;
                  const c = v.verdict === "conforme" ? "#356A0F" : v.verdict === "douteux" ? "#7C5600" : "#A23A0D";
                  return (
                    <span title={v.motif || ""} style={{ background: c, color: "#fff", borderRadius: 6, padding: "1px 6px", fontSize: 9, fontWeight: 700, flexShrink: 0 }}>
                      {v.verdict} {v.score ?? ""}
                    </span>
                  );
                })()}
                {sub.signauxExif && (() => {
                  const e = sub.signauxExif;
                  const t = !e.present ? "EXIF∅"
                    : e.date ? (e.dateConforme === false ? `date ${e.ecartMinutes}m` : "date✓") : "";
                  return t ? <span style={{ color: !e.present ? "#57534A" : e.dateConforme === false ? "#A23A0D" : "#356A0F", fontSize: 9, flexShrink: 0 }}>{t}</span> : null;
                })()}
                {sub.estTest && (
                  <span style={{ background: "#1F1E1B", color: "#fff", borderRadius: 6, padding: "1px 6px", fontSize: 9, fontWeight: 700, flexShrink: 0 }}>TEST</span>
                )}
              </Box>
              {sub.signauxIntegrite && (
                <Box sx={{ mt: 0.5 }}>
                  <IntegriteBadges signaux={sub.signauxIntegrite} exif={sub.signauxExif} />
                </Box>
              )}

              <Typography sx={{ fontSize: "11px", color: C.sub }}>{fmtDateTime(sub.createdAt)}</Typography>

              <Box>
                {photoUrl ? (
                  <Tooltip title="Voir la photo" arrow>
                    <Box component="img" src={photoUrl} alt="photo"
                      onClick={() => window.open(photoUrl, "_blank")}
                      sx={{
                        width: 32, height: 32, borderRadius: "5px", objectFit: "cover",
                        cursor: "pointer", border: `1px solid ${C.borderInput}`,
                        "&:hover": { borderColor: C.accentSoft, opacity: 0.85 },
                      }} />
                  </Tooltip>
                ) : (
                  <Box sx={{
                    width: 32, height: 32, borderRadius: "5px",
                    background: "rgba(31,30,27,0.04)", border: `1px solid ${C.borderInput}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <BrokenImageOutlinedIcon sx={{ fontSize: 14, color: C.muted }} />
                  </Box>
                )}
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "4px" }}>
                <StatusBadge cfg={stCfg} />
                {sub.creditEchec && <CreditEchecBadge message={sub.creditEchecMessage} date={sub.creditEchecLe} />}
              </Box>

              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "6px" }}>
                {sub.creditEchec && sid && (
                  <RelanceCredit type="soumission" id={sid} compact
                    onRelance={() => { loadSubmissions(true); loadStats(); }} />
                )}
                {isPending ? (
                  <>
                    <Button size="small"
                      startIcon={isAct ? <CircularProgress size={10} sx={{ color: C.success }} /> : <CheckRoundedIcon sx={{ fontSize: "12px !important" }} />}
                      disabled={isAct} onClick={() => handleSubmissionAction(sub, "validate")}
                      sx={{
                        background: C.successBg, color: C.success, border: `1px solid ${C.successBdr}`,
                        fontSize: "10px", fontWeight: 700, textTransform: "none",
                        borderRadius: "5px", px: 1.2, py: "4px", minWidth: 0,
                        "&:hover": { background: "rgba(53,106,15,0.16)" },
                        "&.Mui-disabled": { color: C.success, opacity: 0.5 },
                      }}>
                      Valider
                    </Button>
                    <Button size="small"
                      startIcon={isAct ? <CircularProgress size={10} sx={{ color: C.error }} /> : <CloseRoundedIcon sx={{ fontSize: "12px !important" }} />}
                      disabled={isAct} onClick={() => handleSubmissionAction(sub, "reject")}
                      sx={{
                        background: C.errorBg, color: C.error, border: `1px solid ${C.errorBdr}`,
                        fontSize: "10px", fontWeight: 700, textTransform: "none",
                        borderRadius: "5px", px: 1.2, py: "4px", minWidth: 0,
                        "&:hover": { background: "rgba(162,58,13,0.16)" },
                        "&.Mui-disabled": { color: C.error, opacity: 0.5 },
                      }}>
                      Rejeter
                    </Button>
                  </>
                ) : (
                  photoUrl && (
                    <Button size="small"
                      startIcon={<OpenInNewRoundedIcon sx={{ fontSize: "11px !important" }} />}
                      onClick={() => window.open(photoUrl, "_blank")}
                      sx={{
                        background: C.accentBg, color: C.accentSoft, border: `1px solid ${C.accentBorder}`,
                        fontSize: "10px", fontWeight: 700, textTransform: "none",
                        borderRadius: "5px", px: 1.2, py: "4px", minWidth: 0,
                        "&:hover": { background: "rgba(31,30,27,0.18)" },
                      }}>
                      Voir
                    </Button>
                  )
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      {!subLoading && submissions.length > 0 && (
        <Typography sx={{ fontSize: "11px", color: C.muted, mt: 1.5, textAlign: "right" }}>
          {submissions.length} soumission{submissions.length > 1 ? "s" : ""}
        </Typography>
      )}

      {/* ══ CYCLES ══ */}
      {quest?.cycleActif && (
        <Box sx={{ background: C.card, borderRadius: "8px", border: `0.5px solid ${C.accentBorder}`, overflow: "hidden" }}>
          <Box sx={{ px: 2.5, py: "14px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: "8px" }}>
            <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: C.accentSoft, flexShrink: 0 }} />
            <Typography sx={{ fontSize: "13px", fontWeight: 700, color: C.text }}>Cycles</Typography>
            {quest.frequenceCycle && (
              <Box sx={{ fontSize: "10px", color: C.accentSoft, background: C.accentBg, border: `1px solid ${C.accentBorder}`, borderRadius: "8px", px: "6px", py: "1px", fontWeight: 600 }}>
                {quest.frequenceCycle}
              </Box>
            )}
          </Box>

          {cyclesLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 5, gap: 1.5 }}>
              <CircularProgress size={18} sx={{ color: C.accentSoft }} />
              <Typography sx={{ fontSize: "13px", color: C.sub }}>Chargement des cycles…</Typography>
            </Box>
          )}

          {!cyclesLoading && cycles.length === 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 5, gap: 1 }}>
              <Typography sx={{ fontSize: "13px", fontWeight: 600, color: C.text }}>Aucun cycle généré</Typography>
              <Typography sx={{ fontSize: "12px", color: C.sub }}>Les cycles apparaîtront ici au fil du temps.</Typography>
            </Box>
          )}

          {!cyclesLoading && cycles.length > 0 && (
            <>
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 120px 120px 110px 90px", px: 2.5, py: "10px", borderBottom: `1px solid ${C.border}`, background: "rgba(31,30,27,0.02)", alignItems: "center" }}>
                {["Titre", "Date début", "Date fin", "Statut", "Progression"].map((h, i) => (
                  <Typography key={i} sx={{ fontSize: "10px", fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase", color: C.label }}>{h}</Typography>
                ))}
              </Box>
              {cycles.map((cycle: any, idx: number) => {
                const progress = cycle.collectesAttendues > 0
                  ? Math.min(100, Math.round((cycle.collectesRealisees ?? 0) / cycle.collectesAttendues * 100))
                  : 0;
                return (
                  <Box key={cycle.id ?? idx}
                    onClick={() => navigate(`/quetes/${cycle.id}`)}
                    sx={{ display: "grid", gridTemplateColumns: "1fr 120px 120px 110px 90px", px: 2.5, py: "12px", borderBottom: `1px solid ${C.border}`, alignItems: "center", cursor: "pointer", "&:hover": { background: C.rowHover }, "&:last-child": { borderBottom: "none" } }}>
                    <Typography sx={{ fontSize: "13px", color: C.text, fontWeight: 600 }}>
                      {cycle.titre ?? `Cycle ${idx + 1}`}
                    </Typography>
                    <Typography sx={{ fontSize: "12px", color: C.sub }}>
                      {cycle.dateDebut ? new Date(cycle.dateDebut).toLocaleDateString("fr-FR") : "—"}
                    </Typography>
                    <Typography sx={{ fontSize: "12px", color: C.sub }}>
                      {cycle.dateFin ? new Date(cycle.dateFin).toLocaleDateString("fr-FR") : "—"}
                    </Typography>
                    <Box sx={{ display: "inline-flex", alignItems: "center", gap: "5px" }}>
                      <Box sx={{ width: 5, height: 5, borderRadius: "50%", background: questStatusStyle(cycle.statut).color }} />
                      <Typography sx={{ fontSize: "11px", color: C.sub, fontWeight: 600 }}>{questStatusStyle(cycle.statut).label}</Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <LinearProgress variant="determinate" value={progress} sx={{ flex: 1, height: 4, borderRadius: 2, backgroundColor: "rgba(31,30,27,0.08)", "& .MuiLinearProgress-bar": { backgroundColor: C.accentSoft, borderRadius: 2 } }} />
                      <Typography sx={{ fontSize: "10px", color: C.sub, minWidth: "30px" }}>{progress}%</Typography>
                    </Box>
                  </Box>
                );
              })}
            </>
          )}
        </Box>
      )}

      {/* ── Dialog : dépublier pour modifier ── */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)}
        PaperProps={{ sx: { background: "var(--c-surface)", border: "1.5px solid #1F1E1B", borderRadius: "8px", color: C.text, minWidth: 380 } }}>
        <DialogTitle sx={{ fontSize: "15px", fontWeight: 700, color: C.text, pb: 1 }}>
          Modifier cette quête
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "13px", color: C.sub }}>
            Pour modifier cette quête, vous devez d'abord la dépublier. Elle ne sera plus visible dans l'application le temps de la modification.
            <br /><br />
            <strong style={{ color: C.gold }}>Dépublier maintenant et modifier ?</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setEditDialog(false)}
            sx={{
              color: C.sub, fontSize: "12px", fontWeight: 600, textTransform: "none",
              borderRadius: "6px", px: 2,
              "&:hover": { background: "rgba(31,30,27,0.06)" },
            }}>
            Annuler
          </Button>
          <Button onClick={handleUnpublishAndEdit}
            sx={{
              background: C.goldBg, color: C.gold, border: `1px solid ${C.goldBdr}`,
              fontSize: "12px", fontWeight: 700, textTransform: "none",
              borderRadius: "6px", px: 2,
              "&:hover": { background: "rgba(124,86,0,0.18)", borderColor: C.gold },
            }}>
            Dépublier et modifier
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog : confirmation suppression ── */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog(d => ({ ...d, open: false }))}
        PaperProps={{ sx: { background: "var(--c-surface)", border: "1.5px solid #1F1E1B", borderRadius: "8px", color: C.text, minWidth: 360 } }}>
        <DialogTitle sx={{ fontSize: "15px", fontWeight: 700, color: C.text, pb: 1 }}>
          {confirmDialog.title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "13px", color: C.sub }}>
            {confirmDialog.body}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setConfirmDialog(d => ({ ...d, open: false }))}
            sx={{
              color: C.sub, fontSize: "12px", fontWeight: 600, textTransform: "none",
              borderRadius: "6px", px: 2,
              "&:hover": { background: "rgba(31,30,27,0.06)" },
            }}>
            Annuler
          </Button>
          <Button onClick={confirmDialog.onConfirm}
            sx={{
              background: C.errorBg, color: C.error, border: `1px solid ${C.errorBdr}`,
              fontSize: "12px", fontWeight: 700, textTransform: "none",
              borderRadius: "6px", px: 2,
              "&:hover": { background: "rgba(162,58,13,0.16)", borderColor: C.error },
            }}>
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog : désigner le gagnant (challenge) ── */}
      <Dialog
        open={winnerDialogOpen}
        onClose={() => { if (!designatingWinner) setWinnerDialogOpen(false); }}
        PaperProps={{ sx: { background: "var(--c-surface)", border: "1px solid rgba(31,30,27,0.25)", borderRadius: "8px", color: C.text, minWidth: 400 } }}
      >
        <DialogTitle sx={{ fontSize: "15px", fontWeight: 700, color: C.text, pb: 1 }}>
          🏆 Désigner le gagnant
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "13px", color: C.sub, mb: 2 }}>
            Sélectionnez la soumission gagnante parmi les soumissions validées.
            {Number(quest.challengeRecompenseTips) > 0
              ? ` Le gagnant reçoit ${Number(quest.challengeRecompenseTips).toLocaleString("fr-FR")} Tips.`
              : ""}
          </DialogContentText>
          <FormControl fullWidth size="small" sx={{
            "& .MuiOutlinedInput-root": {
              background: "var(--c-surface)", color: C.text, borderRadius: "8px", fontSize: "13px",
              "& fieldset": { borderColor: "rgba(31,30,27,0.12)" },
              "&:hover fieldset": { borderColor: "rgba(31,30,27,0.40)" },
              "&.Mui-focused fieldset": { borderColor: C.accent, borderWidth: "1px" },
            },
            "& .MuiSelect-icon": { color: C.sub },
          }}>
            <Select
              value={selectedWinner}
              onChange={e => setSelectedWinner(e.target.value)}
              displayEmpty
              MenuProps={{
                PaperProps: {
                  sx: {
                    background: "var(--c-surface)", border: `1px solid rgba(31,30,27,0.12)`,
                    borderRadius: "8px", color: C.text,
                    "& .MuiMenuItem-root": {
                      fontSize: "13px", py: "8px",
                      "&:hover": { background: C.accentBg },
                      "&.Mui-selected": { background: C.accentBg, color: C.accentSoft },
                    },
                  },
                },
              }}
              renderValue={v => {
                if (!v) return <span style={{ color: C.muted }}>Choisir une soumission…</span>;
                const sub = candidatsGagnant.find(s => (s.id ?? "") === v);
                return sub ? (sub.collecteurEmail ?? sub.collecteurNom ?? v) : v;
              }}
            >
              {candidatsGagnant.length === 0 && (
                <MenuItem disabled value="">Aucune soumission validée</MenuItem>
              )}
              {candidatsGagnant.map(sub => {
                const sid = getId(sub);
                return (
                  <MenuItem key={sid} value={sid}>
                    <Box>
                      <Typography sx={{ fontSize: "13px", color: C.text }}>
                        {sub.collecteurEmail ?? sub.collecteurNom ?? sid}
                      </Typography>
                      <Typography sx={{ fontSize: "10px", color: C.muted }}>
                        {fmtDateTime(sub.createdAt)} · {sub.statut}
                      </Typography>
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setWinnerDialogOpen(false)}
            disabled={designatingWinner}
            sx={{
              color: C.sub, fontSize: "12px", fontWeight: 600, textTransform: "none",
              borderRadius: "6px", px: 2,
              "&:hover": { background: "rgba(31,30,27,0.06)" },
            }}>
            Annuler
          </Button>
          <Button
            onClick={handleDesignerGagnant}
            disabled={!selectedWinner || designatingWinner}
            startIcon={designatingWinner ? <CircularProgress size={12} sx={{ color: C.accentSoft }} /> : undefined}
            sx={{
              background: C.accentBg, color: C.accentSoft, border: `1px solid ${C.accentBorder}`,
              fontSize: "12px", fontWeight: 700, textTransform: "none",
              borderRadius: "6px", px: 2,
              "&:hover": { background: "rgba(31,30,27,0.18)", borderColor: C.accentSoft },
              "&.Mui-disabled": { color: C.accentSoft, opacity: 0.5 },
            }}>
            {designatingWinner ? "Enregistrement…" : "Continuer"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar succès gagnant ── */}
      <Snackbar
        open={winnerSnackbar}
        autoHideDuration={4000}
        onClose={() => setWinnerSnackbar(false)}
        message="🏆 Gagnant désigné avec succès !"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        ContentProps={{
          sx: {
            background: C.accentBg, border: `1px solid ${C.accentBorder}`,
            color: C.accentSoft, fontWeight: 600, borderRadius: "8px",
          },
        }}
      />
    </Box>
  );
}
